<template>
  <div
    class="island-play"
    :class="{
      'island-play--sand-open': sandPropsOpen,
      'island-play--tour': tourActive,
      'island-play--tour-ready': tourActive && !tourTransitioning,
      'island-play--locked': timerExpired,
      'island-play--landscape-required': forceLandscape
    }"
  >
    <transition name="landscape-fade">
      <div
        v-if="forceLandscape"
        class="landscape-prompt"
        role="alertdialog"
        aria-live="polite"
        aria-label="请将设备横屏以获得最佳体验"
        @click="tryLockLandscape"
      >
        <div class="landscape-prompt__card">
          <div class="landscape-prompt__icon" aria-hidden="true">
            <span class="landscape-prompt__phone" />
            <span class="landscape-prompt__arrow" />
          </div>
<div class="landscape-prompt__title">请将设备旋转至横屏</div>
        <div class="landscape-prompt__hint">本页面在横屏下体验最佳</div>
        <div class="landscape-prompt__sub">如屏幕未自动旋转，请开启系统“自动旋转”，或点击下方按钮尝试</div>
        <button
          type="button"
          class="landscape-prompt__btn landscape-prompt__btn--ghost"
          @click.stop="dismissLandscapePrompt"
        >仍然进入</button>
        </div>
      </div>
    </transition>
    <island-playground-three
      ref="playground"
      class="island-play__three"
      :terrain-visible="terrainVisible"
      :terrain-tool="timerExpired ? null : activeTerrainTool"
      :terrain-material="activeMaterialId"
      :weather="activeWeatherId"
      :sand-place-item="timerExpired ? null : selectedSandItem"
      :pointer-capture-disabled="propEditHudSliderLock"
      @terrain-interact="onTerrainInteract"
      @terrain-sculpt-height="onTerrainSculptHeight"
      @sand-prop-placed="onSandPropPlaced"
      @sand-prop-edit="onSandPropEdit"
      @tour-mode-change="onTourModeChange"
      @tour-water-change="onTourWaterChange"
      @camera-orbit-change="syncCamSlidersFromCamera"
    />

    <div
      v-if="propEditHud && propEditHud.active && !tourActive"
      class="prop-edit-root"
      :style="propEditHudStyle"
      aria-label="沙具编辑"
    >
      <div class="prop-edit-focus" aria-hidden="true" />
      <div
        class="prop-edit-pitch"
        aria-label="沙具俯仰"
        @touchstart.stop="onPropEditSliderTouch(true, $event)"
        @touchend.stop="onPropEditSliderLock(false)"
        @touchcancel.stop="onPropEditSliderLock(false)"
        @pointerdown.stop="onPropEditSliderLock(true, $event)"
        @pointerup.stop="onPropEditSliderLock(false)"
        @pointercancel.stop="onPropEditSliderLock(false)"
      >
        <span class="prop-edit-pitch__tag">俯</span>
        <input
          type="range"
          class="prop-edit-pitch__range"
          min="0"
          max="100"
          step="1"
          :value="propPitch"
          aria-label="沙具俯仰"
          @input="onPropPitchInput"
        >
        <span class="prop-edit-pitch__tag">仰</span>
      </div>
      <div
        class="prop-edit-yaw"
        aria-label="沙具左右旋转"
        @touchstart.stop="onPropEditSliderTouch(true, $event)"
        @touchend.stop="onPropEditSliderLock(false)"
        @touchcancel.stop="onPropEditSliderLock(false)"
        @pointerdown.stop="onPropEditSliderLock(true, $event)"
        @pointerup.stop="onPropEditSliderLock(false)"
        @pointercancel.stop="onPropEditSliderLock(false)"
      >
        <span class="prop-edit-yaw__tag">左</span>
        <input
          type="range"
          class="prop-edit-yaw__range"
          min="0"
          max="100"
          step="1"
          :value="propYaw"
          aria-label="沙具左右旋转"
          @input="onPropYawInput"
        >
        <span class="prop-edit-yaw__tag">右</span>
      </div>
      <div
        class="prop-edit-height"
        aria-label="沙具高度"
        @touchstart.stop="onPropEditSliderTouch(true, $event)"
        @touchend.stop="onPropEditSliderLock(false)"
        @touchcancel.stop="onPropEditSliderLock(false)"
        @pointerdown.stop="onPropEditSliderLock(true, $event)"
        @pointerup.stop="onPropEditSliderLock(false)"
        @pointercancel.stop="onPropEditSliderLock(false)"
      >
        <span class="prop-edit-height__tag">天</span>
        <input
          type="range"
          class="prop-edit-height__range"
          min="0"
          max="100"
          step="1"
          :value="propHeight"
          aria-label="沙具高度"
          @input="onPropHeightInput"
        >
        <span class="prop-edit-height__tag">地</span>
      </div>
      <button
        type="button"
        class="prop-edit-btn prop-edit-btn--scale-up"
        title="放大"
        aria-label="放大"
        @click.stop="propEditAction('scaleUp')"
      >
        +
      </button>
      <button
        type="button"
        class="prop-edit-btn prop-edit-btn--scale-down"
        title="缩小"
        aria-label="缩小"
        @click.stop="propEditAction('scaleDown')"
      >
        −
      </button>
      <button
        type="button"
        class="prop-edit-btn prop-edit-btn--cancel prop-edit-btn--label"
        title="取消"
        aria-label="取消编辑"
        @click.stop="propEditAction('cancel')"
      >
        取消
      </button>
      <button
        type="button"
        class="prop-edit-btn prop-edit-btn--delete prop-edit-btn--label"
        title="删除"
        aria-label="删除"
        @click.stop="propEditAction('delete')"
      >
        删除
      </button>
      <button
        type="button"
        class="prop-edit-btn prop-edit-btn--confirm prop-edit-btn--label"
        title="完成"
        aria-label="完成编辑"
        @click.stop="propEditAction('confirm')"
      >
        确认
      </button>
    </div>

    <p v-if="selectedSandItem && !(propEditHud && propEditHud.active)" class="island-play__hint">
      已选「{{ selectedSandItem.label }}」，点击左侧绿色沙盘放置
    </p>

    <div
      v-show="!tourActive"
      class="cam-controls"
      aria-label="视角控制"
      @touchstart.stop
      @pointerdown.stop="onCamControlPointerDown"
    >
      <div class="cam-control cam-control--pitch">
        <span class="cam-control__tag">仰</span>
        <div class="cam-control__vwrap">
          <input
            type="range"
            class="cam-control__range cam-control__range--v"
            min="0"
            max="100"
            step="1"
            :value="camPitch"
            aria-label="视角俯仰"
            @input="onCamPitchInput"
          >
        </div>
        <span class="cam-control__tag">俯</span>
      </div>
      <div class="cam-control cam-control--yaw">
        <span class="cam-control__tag">左</span>
        <input
          ref="camYawRange"
          type="range"
          class="cam-control__range cam-control__range--h"
          min="-180"
          max="180"
          step="1"
          :value="camYawDeg"
          aria-label="地面左右旋转"
          @input="onCamYawInput"
        >
        <span class="cam-control__tag">右</span>
      </div>
      <div class="cam-control cam-control--zoom">
        <span class="cam-control__tag">放大</span>
        <div class="cam-control__vwrap">
          <input
            ref="camZoomRange"
            type="range"
            class="cam-control__range cam-control__range--v"
            min="0"
            max="100"
            step="1"
            :value="camZoom"
            aria-label="地面缩放"
            @input="onCamZoomInput"
          >
        </div>
        <span class="cam-control__tag">缩小</span>
      </div>
    </div>

    <header class="island-play__top">
      <div class="island-play__top-line">
        <button
          type="button"
          class="icon-btn icon-btn--square"
          title="返回主页"
          aria-label="返回主页"
          @click="goBack"
        >
          <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">
            <path
              fill="currentColor"
              d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z"
            />
          </svg>
        </button>

        <div class="timer" :class="{ 'timer--expired': timerExpired }" aria-live="polite">
          <span class="timer__icon" aria-hidden="true">⏱</span>
          <span class="timer__text">{{ timerText }}</span>
        </div>

        <div class="top-actions">
          <button
            type="button"
            class="pill-btn"
            :class="{ 'pill-btn--active': tourActive }"
            :disabled="tourTransitioning"
            @click="toggleTour"
          >
            <span class="pill-btn__ic" aria-hidden="true">{{ tourActive ? '↩' : '🔍' }}</span>
            {{ tourActive ? '退出游览' : '游览' }}
          </button>
          <button type="button" class="pill-btn" @click="openPrivacyGuide">
            <span class="pill-btn__ic" aria-hidden="true">!</span>
            说明
          </button>
        </div>
      </div>
    </header>

    <div v-if="tourActive" class="tour-bar" role="status">
      <p class="tour-bar__hint">
        {{ tourTransitioning
          ? '正在切换视角…'
          : (isMobileLayout() ? tourMobileHint : tourHintText) }}
      </p>
      <!-- 桌面端：原 4 方向按钮（依赖键盘 + 鼠标 + Pointer Lock） -->
      <div
        v-show="!tourTransitioning && !isMobileLayout()"
        class="tour-pad"
        aria-label="移动"
        @touchstart.stop
        @touchend.stop
        @touchcancel.stop
        @pointerdown.stop
        @pointerup.stop
        @pointercancel.stop
        @pointerleave.stop
      >
        <button
          type="button"
          class="tour-pad__btn tour-pad__btn--fwd"
          aria-label="前进"
          @touchstart.prevent="setTourMove('forward', true)"
          @touchend.prevent="setTourMove('forward', false)"
          @touchcancel.prevent="setTourMove('forward', false)"
          @pointerdown.prevent="setTourMove('forward', true)"
          @pointerup.prevent="setTourMove('forward', false)"
          @pointercancel.prevent="setTourMove('forward', false)"
          @pointerleave.prevent="setTourMove('forward', false)"
        >
          ↑
        </button>
        <button
          type="button"
          class="tour-pad__btn tour-pad__btn--left"
          aria-label="左移"
          @touchstart.prevent="setTourMove('left', true)"
          @touchend.prevent="setTourMove('left', false)"
          @touchcancel.prevent="setTourMove('left', false)"
          @pointerdown.prevent="setTourMove('left', true)"
          @pointerup.prevent="setTourMove('left', false)"
          @pointercancel.prevent="setTourMove('left', false)"
          @pointerleave.prevent="setTourMove('left', false)"
        >
          ←
        </button>
        <button
          type="button"
          class="tour-pad__btn tour-pad__btn--back"
          aria-label="后退"
          @touchstart.prevent="setTourMove('back', true)"
          @touchend.prevent="setTourMove('back', false)"
          @touchcancel.prevent="setTourMove('back', false)"
          @pointerdown.prevent="setTourMove('back', true)"
          @pointerup.prevent="setTourMove('back', false)"
          @pointercancel.prevent="setTourMove('back', false)"
          @pointerleave.prevent="setTourMove('back', false)"
        >
          ↓
        </button>
        <button
          type="button"
          class="tour-pad__btn tour-pad__btn--right"
          aria-label="右移"
          @touchstart.prevent="setTourMove('right', true)"
          @touchend.prevent="setTourMove('right', false)"
          @touchcancel.prevent="setTourMove('right', false)"
          @pointerdown.prevent="setTourMove('right', true)"
          @pointerup.prevent="setTourMove('right', false)"
          @pointercancel.prevent="setTourMove('right', false)"
          @pointerleave.prevent="setTourMove('right', false)"
        >
          →
        </button>
      </div>
      <!-- 移动端：虚拟摇杆（左下）；松手自动回中 -->
      <div
        v-show="!tourTransitioning && isMobileLayout()"
        ref="tourJoystick"
        class="tour-joystick"
        role="group"
        aria-label="移动摇杆"
        @pointerdown.stop.prevent="onJoystickPointerDown"
        @pointermove.stop.prevent="onJoystickPointerMove"
        @pointerup.stop.prevent="onJoystickPointerUp"
        @pointercancel.stop.prevent="onJoystickPointerUp"
        @lostpointercapture.stop.prevent="onJoystickPointerUp"
      >
        <div
          class="tour-joystick__knob"
          :style="joystickKnobStyle"
          aria-hidden="true"
        />
        <span class="tour-joystick__hint">{{ joystickHintText }}</span>
      </div>
    </div>

    <transition name="sand-sheet">
      <div
        v-show="sandPropsOpen"
        class="sand-sheet-root"
        role="dialog"
        aria-modal="true"
        aria-label="沙具与天气"
      >
        <div
          class="sand-sheet"
          @touchstart.stop
        >
          <button
            type="button"
            class="sand-sheet__collapse"
            aria-label="收起沙具面板"
            @click="closeSandPropsPanel"
          >
            <svg viewBox="0 0 24 24" width="24" height="24" aria-hidden="true">
              <path
                fill="currentColor"
                d="M8.59 16.59L10 18l6-6-6-6-1.41 1.41L13.17 12z"
              />
            </svg>
          </button>
          <div class="sand-sheet__head">
            <p v-if="sandDevBuildTag" class="sand-sheet__build-tag">{{ sandDevBuildTag }}</p>
            <div v-if="sandActiveTab !== 'weather'" class="sand-sheet__search">
              <input
                v-model.trim="sandSearchInput"
                type="text"
                class="sand-sheet__search-input"
                placeholder="搜索"
                autocomplete="off"
                @keydown.enter.prevent="runSandSearch"
              >
              <button
                v-if="sandSearchInput"
                type="button"
                class="sand-sheet__search-clear"
                aria-label="清空搜索"
                @click="clearSandSearch"
              >
                <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
                  <path fill="currentColor" d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                </svg>
              </button>
              <button
                type="button"
                class="sand-sheet__search-go"
                aria-label="搜索"
                @click="runSandSearch"
              >
                <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
                  <path
                    fill="currentColor"
                    d="M15.5 14h-.79l-.28-.27A6.47 6.47 0 0 0 16 9.5 6.5 6.5 0 1 0 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"
                  />
                </svg>
              </button>
            </div>
            <nav class="sand-tabs" aria-label="沙具分类">
              <button
                v-for="tab in sandTabs"
                :key="tab.key"
                type="button"
                class="sand-tab"
                :class="{ 'sand-tab--on': sandActiveTab === tab.key }"
                @click="selectSandTab(tab.key)"
              >
                {{ tab.label }}
              </button>
            </nav>
          </div>
          <div class="sand-sheet__grid-wrap">
            <div
              v-if="sandActiveTab === 'weather'"
              class="sand-weather-grid"
              role="toolbar"
              aria-label="天气选项"
            >
              <button
                v-for="w in weatherOptions"
                :key="w.id"
                type="button"
                class="sand-weather-cell"
                :class="{ 'sand-weather-cell--on': activeWeatherId === w.id }"
                :aria-pressed="activeWeatherId === w.id"
                @click="selectWeather(w.id)"
              >
                <span class="sand-weather-cell__ic" aria-hidden="true">{{ w.emoji }}</span>
                <span class="sand-weather-cell__label">{{ w.label }}</span>
              </button>
            </div>
            <template v-else>
              <div class="sand-grid" data-sand-catalog="v12">
                <button
                  v-for="item in sandFilteredItems"
                  :key="getSandItemKey(sandActiveTab, item)"
                  type="button"
                  class="sand-cell"
                  :class="{ 'sand-cell--on': isSandItemSelected(item) }"
                  :aria-pressed="isSandItemSelected(item)"
                  @click="selectSandItem(item)"
                >
                  <span class="sand-cell__thumb" aria-hidden="true">
                    <img
                      v-if="item.thumbUrl"
                      :src="item.thumbUrl"
                      class="sand-cell__thumb-img"
                      loading="lazy"
                      decoding="async"
                      alt=""
                    >
                    <template v-else>
                      <span class="sand-cell__thumb-emoji">{{ item.emoji }}</span>
                      <span v-if="item.modelKey || item.modelUrl" class="sand-cell__model-tag">3D</span>
                    </template>
                  </span>
                  <span class="sand-cell__label">{{ item.label }}</span>
                </button>
              </div>
              <p v-if="!sandFilteredItems.length" class="sand-sheet__empty">暂无匹配素材</p>
            </template>
          </div>
        </div>
      </div>
    </transition>

    <footer v-show="!tourActive" class="island-play__bottom">
      <div class="island-play__dock-row">
        <div
          class="terrain-dock"
          :class="{
            'terrain-dock--open': terrainToolbarOpen,
            'terrain-dock--material': materialPaletteOpen
          }"
        >
          <div class="terrain-dock__surface">
            <button
              v-if="!terrainToolbarOpen"
              type="button"
              key="terrain-collapsed"
              class="dock-btn dock-btn--terrain"
              @click="toggleTerrainToolbar"
            >
              <span class="dock-btn__ic" aria-hidden="true">⛰</span>
              地形
            </button>
            <div
              v-else
              key="terrain-expanded"
              class="terrain-toolbar"
              :class="{ 'terrain-toolbar--material-open': materialPaletteOpen }"
              role="toolbar"
              aria-label="地形工具"
            >
              <button
                type="button"
                class="terrain-toolbar__back"
                :title="materialPaletteOpen ? '返回工具' : '收起'"
                :aria-label="materialPaletteOpen ? '返回地形工具' : '收起地形工具'"
                @click="onTerrainToolbarBack"
              >
                <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">
                  <path
                    fill="currentColor"
                    d="M11.18 12l5.66 5.66-1.41 1.41L8.35 12l7.07-7.07 1.41 1.41L11.18 12zm-6 0l5.66 5.66-1.41 1.41L2.35 12l7.07-7.07 1.41 1.41L5.18 12z"
                  />
                </svg>
              </button>
              <div class="terrain-toolbar__tools">
                <button
                  type="button"
                  class="terrain-tool"
                  :class="{ 'terrain-tool--on': activeTerrainTool === 'sculpt' }"
                  @click="selectTerrainTool('sculpt')"
                >
                  <span class="terrain-tool__icon terrain-tool__icon--sculpt" aria-hidden="true">
                    <svg viewBox="0 0 48 48" width="36" height="36">
                      <path fill="#5d4037" d="M8 38h32v4H8z" />
                      <path fill="#7cb342" d="M10 28 L24 12 L38 28 v10H10z" />
                      <path fill="#eceff1" d="M18 14 L24 8 L30 14 L24 20z" />
                      <path fill="#c62828" d="M20 26h3v3h-3zm8 2h3v3h-3z" />
                    </svg>
                  </span>
                  <span class="terrain-tool__label">堆沙</span>
                </button>
                <button
                  type="button"
                  class="terrain-tool"
                  :class="{ 'terrain-tool--on': activeTerrainTool === 'water' }"
                  @click="selectTerrainTool('water')"
                >
                  <span class="terrain-tool__icon terrain-tool__icon--water" aria-hidden="true">
                    <svg viewBox="0 0 48 48" width="36" height="36">
                      <ellipse fill="#a1887f" cx="24" cy="24" rx="18" ry="16" />
                      <ellipse fill="#81d4fa" cx="24" cy="25" rx="12" ry="10" />
                      <path
                        fill="none"
                        stroke="#e3f2fd"
                        stroke-width="1.2"
                        d="M14 24c2-1 4 1 6 0s4-1 6 0 4 1 6 0 4-1 6 0"
                      />
                    </svg>
                  </span>
                  <span class="terrain-tool__label">水面</span>
                </button>
                <button
                  type="button"
                  class="terrain-tool"
                  :class="{ 'terrain-tool--on': activeTerrainTool === 'flatten' }"
                  @click="selectTerrainTool('flatten')"
                >
                  <span class="terrain-tool__icon terrain-tool__icon--flatten" aria-hidden="true">
                    <svg viewBox="0 0 48 48" width="36" height="36">
                      <ellipse fill="#1b5e20" cx="24" cy="28" rx="16" ry="5" />
                      <ellipse fill="#43a047" cx="24" cy="22" rx="14" ry="10" />
                      <circle fill="#2e7d32" cx="16" cy="20" r="1.2" />
                      <circle fill="#66bb6a" cx="22" cy="18" r="1" />
                      <circle fill="#2e7d32" cx="30" cy="21" r="1.1" />
                      <circle fill="#1b5e20" cx="26" cy="24" r="0.9" />
                    </svg>
                  </span>
                  <span class="terrain-tool__label">平整</span>
                </button>
                <button
                  type="button"
                  class="terrain-tool"
                  :class="{ 'terrain-tool--on': activeTerrainTool === 'material' }"
                  @click="selectTerrainTool('material')"
                >
                  <span class="terrain-tool__icon terrain-tool__icon--material" aria-hidden="true">
                    <svg viewBox="0 0 48 48" width="36" height="36">
                      <rect x="6" y="10" width="28" height="22" rx="3" fill="#f8bbd0" opacity="0.85" />
                      <rect x="10" y="14" width="8" height="8" rx="1" fill="#fff59d" />
                      <rect x="22" y="16" width="8" height="6" rx="1" fill="#a5d6a7" />
                      <path fill="#d7ccc8" d="M28 32l12-14 3 3-10 16-6 2 1-7z" />
                      <path fill="#fafafa" d="M36 20l2 2-8 12-4 1 1-4 9-11z" />
                    </svg>
                  </span>
                  <span class="terrain-tool__label">材质</span>
                </button>
                <div
                  v-if="materialPaletteOpen"
                  class="terrain-material-palette"
                  role="group"
                  aria-label="选择地表材质"
                >
                  <button
                    v-for="m in terrainMaterials"
                    :key="m.id"
                    type="button"
                    class="terrain-material"
                    :class="{ 'terrain-material--on': activeMaterialId === m.id }"
                    :title="m.label"
                    @click="selectTerrainMaterial(m.id)"
                  >
                    <span
                      class="terrain-material__swatch"
                      :class="'terrain-material__swatch--' + m.id"
                      aria-hidden="true"
                    />
                    <span class="terrain-material__label">{{ m.label }}</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <button
          type="button"
          class="dock-btn dock-btn--sand"
          :class="{ 'dock-btn--sand-on': sandPropsOpen }"
          :aria-pressed="sandPropsOpen"
          @click="toggleSandPropsPanel"
        >
          <span class="dock-btn__ic" aria-hidden="true">🏖</span>
          沙具
        </button>
      </div>
    </footer>

    <save-name-dialog
      :visible.sync="saveNameOpen"
      :remaining="saveRemaining"
      :total-quota="saveTotal"
      :loading="saving"
      :age="userAge"
      :questions="saveDialogQuestions"
      @confirm="onSaveNameConfirm"
      @cancel="onSaveNameCancel"
    />

    <confirm-dialog
      :visible.sync="exitCancelConfirmOpen"
      title="确认取消"
      message="确认取消保存将丢失所有操作记录"
      confirm-text="确认取消"
      cancel-text="返回保存"
      variant="danger"
      @confirm="confirmDiscardAndLeave"
      @cancel="reopenSaveNameDialog"
    />

    <transition name="save-success">
      <div
        v-if="saveSuccessOpen"
        class="save-success-overlay"
      >
        <div class="save-success-card">
          <div class="save-success-icon">
            <svg viewBox="0 0 24 24" width="48" height="48">
              <path
                fill="currentColor"
                d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"
              />
            </svg>
          </div>
          <p class="save-success-message">{{ saveSuccessMessage }}</p>
        </div>
      </div>
    </transition>

    <div class="privacy-wrapper">
      <transition name="privacy-fade">
        <div v-if="showPrivacy" class="privacy-overlay">
          <transition name="privacy-img-fade">
            <img class="privacy-bg" :key="currentPrivacyImg" :class="{ 'privacy-bg--sm': privacyStep === 3, 'privacy-bg--step4': privacyStep === 3, 'privacy-bg--step5': privacyStep === 4, 'privacy-bg--step2-tool': privacyStep === 1, 'privacy-bg--step3-terrain': privacyStep === 2 }" alt="说明" :src="currentPrivacyImg">
          </transition>
          <img class="privacy-next-btn" :src="nextBtnUrl" alt="下一步" @click="onPrivacyNext">
        </div>
      </transition>
    </div>
  </div>
</template>

<script>
import ConfirmDialog from '@/components/ConfirmDialog.vue'
import SaveNameDialog from '@/components/SaveNameDialog.vue'
import IslandPlaygroundThree from '@/components/IslandPlaygroundThree.vue'
import mengcengBg from '@/assets/mengceng/说明1.png'
import nextBtnUrl from '@/assets/mengceng/btn.png'
import mengcengBg2 from '@/assets/mengceng/说明2-1-沙具选择与放置.png'
import mengcengBg3 from '@/assets/mengceng/说明2-工具选择.png'
import mengcengBg4 from '@/assets/mengceng/说明2-2-沙具调整.png'
import mengcengBg5 from '@/assets/mengceng/说明3-地形工具.png'
import { getSandTableData, uploadSandTableData } from '@/api/psychology'
import { uploadUserFile } from '@/api/user'
import message from '@/utils/message'
import { isUnauthorizedError } from '@/utils/request'
import { getUser, getSelectedClass, clearUser } from '@/utils/auth'
import { getClassSlotKey, getSandSession, saveSandSession } from '@/utils/sandSession'
import { buildSandTablePayload } from '@/utils/sandTableSave'
import {
  hasSavedScene,
  normalizeSandTableResponse
} from '@/utils/sandTableRestore'
import { SAND_MODEL_PRELOAD } from '@/config/sandModels'
import { getSandItemKey as buildSandItemKey, SAND_CATALOG } from '@/config/sandCatalog'
import { preloadSandModels, preloadSandThumbImages, prefetchSandModel } from '@/utils/sandModelLoader'

function pad (n) {
  return n < 10 ? '0' + n : String(n)
}

const TERRAIN_MATERIALS = [
  { id: 'grass', label: '草地' },
  { id: 'sand', label: '沙地' },
  { id: 'snow', label: '雪地' },
  { id: 'brick', label: '砖块' },
  { id: 'rock', label: '岩石' }
]

const PROP_PITCH_MAX = Math.PI / 2
/** 左右滑条：中点 50，两端各 ±180° — Cursor Write It */
const PROP_YAW_DELTA_MAX = Math.PI
/** 与沙盘 sculpt 高度一致：0=地面，50=最高，-10=挖至最低 — Cursor Write It */
const SCULPT_MAX_Y = 0.58
const PROP_HEIGHT_MIN = -SCULPT_MAX_Y
const PROP_HEIGHT_MAX = SCULPT_MAX_Y

const INITIAL_REMAIN_SEC = 40 * 60

/** 保存沙盘每日可用次数上限 — Cursor Write It */
const SAVE_QUOTA_TOTAL = 20
const SAVE_QUOTA_STORAGE_KEY = 'sandplay_save_quota_remaining'
const SAVE_QUOTA_DATE_KEY = 'sandplay_save_quota_date'

function readSaveQuota () {
  try {
    const today = new Date()
    const todayKey =
      today.getFullYear() + '-' + (today.getMonth() + 1) + '-' + today.getDate()
    const storedDate = sessionStorage.getItem(SAVE_QUOTA_DATE_KEY)
    if (storedDate !== todayKey) {
      sessionStorage.setItem(SAVE_QUOTA_DATE_KEY, todayKey)
      sessionStorage.setItem(SAVE_QUOTA_STORAGE_KEY, String(SAVE_QUOTA_TOTAL))
      return SAVE_QUOTA_TOTAL
    }
    const raw = parseInt(sessionStorage.getItem(SAVE_QUOTA_STORAGE_KEY), 10)
    if (isNaN(raw)) return SAVE_QUOTA_TOTAL
    return Math.max(0, Math.min(SAVE_QUOTA_TOTAL, raw))
  } catch (e) {
    return SAVE_QUOTA_TOTAL
  }
}

function writeSaveQuota (value) {
  try {
    sessionStorage.setItem(SAVE_QUOTA_STORAGE_KEY, String(Math.max(0, value)))
  } catch (e) {}
}

const WEATHER_OPTIONS = [
  { id: 'day-clear', label: '白天(晴)', emoji: '☀️' },
  { id: 'day-cloudy', label: '白天(多云)', emoji: '⛅' },
  { id: 'day-rain', label: '白天(降水)', emoji: '🌧️' },
  { id: 'day-snow', label: '白天(降雪)', emoji: '🌨️' },
  { id: 'night-clear', label: '夜晚(晴)', emoji: '🌙' },
  { id: 'night-cloudy', label: '夜晚(多云)', emoji: '☁️' },
  { id: 'night-rain', label: '夜晚(降水)', emoji: '🌧️' },
  { id: 'night-snow', label: '夜晚(降雪)', emoji: '❄️' }
]

export default {
  name: 'IslandPlay',
  components: {
    ConfirmDialog,
    SaveNameDialog,
    IslandPlaygroundThree
  },
  data () {
    return {
      terrainVisible: true,
      terrainToolbarOpen: false,
      activeTerrainTool: null,
      materialPaletteOpen: false,
      activeMaterialId: 'grass',
      terrainMaterials: TERRAIN_MATERIALS,
      sculptHeightMeters: 0,
      sandPropsOpen: false,
      sandDevBuildTag: process.env.NODE_ENV === 'development' ? '本地开发 · v45 · 沙具缓存207' : '',
      sandTabs: [
        { key: 'character', label: '人物' },
        { key: 'animal', label: '动物' },
        { key: 'plant', label: '植物' },
        { key: 'artificial', label: '人造' },
        { key: 'transport', label: '交通' },
        { key: 'weather', label: '天气' }
      ],
      sandActiveTab: 'character',
      sandSearchInput: '',
      sandSearchQuery: '',
      selectedSandItem: null,
      propEditHud: null,
      activeWeatherId: 'day-clear',
      weatherOptions: WEATHER_OPTIONS,
      remainSec: INITIAL_REMAIN_SEC,
      initialRemainSec: INITIAL_REMAIN_SEC,
      timerId: null,
      exitConfirmOpen: false,
      exitCancelConfirmOpen: false,
      timerExpired: false,
      saveNameOpen: false,
      saveRemaining: 20,
      saveTotal: SAVE_QUOTA_TOTAL,
      saving: false,
      saveDialogQuestions: [],
      saveSuccessOpen: false,
      saveSuccessMessage: '',
      restoreLoading: false,
      userAge: 0,
      mengcengBg,
      mengcengBg2,
      mengcengBg3,
      mengcengBg4,
      mengcengBg5,
      nextBtnUrl,
      showPrivacy: false,
      privacyStep: 0,
      privacySteps: [mengcengBg, mengcengBg3, mengcengBg5, mengcengBg2, mengcengBg4],
      camYawDeg: 0,
      camPitch: 50,
      camZoom: 50,
      propYaw: 50,
      propPitch: 50,
      propHeight: 50,
      propEditBase: null,
      propEditHudSliderLock: false,
      camOrbitSyncLock: false,
      tourActive: false,
      tourTransitioning: false,
      tourInWater: false,
      forceLandscape: false,
      landscapePromptDismissed: false,
      // 移动端游览模式下的虚拟摇杆 + 屏幕滑屏视角
      tourJoystick: { activePointerId: null, dx: 0, dy: 0 },
      tourLookTouch: { activePointerId: null, lastX: 0, lastY: 0 },
      // 移动端双指缩放 + 旋转地面（pinch + rotate）
      pinchState: {
        pointers: Object.create(null), // pointerId -> { x, y }
        firstTwoIds: null, // 固定使用最早按下的两个手指 ID
        lastDist: 0,
        baseDist: 0, // 固定基准距离，不随 move 变化
        lastAngle: 0, // 两指连线在屏幕坐标系的角度（弧度）
        baseAngle: 0, // 固定基准角度
        suppressSingleFinger: false,
        initialized: false,
        baseRadius: 3,
        baseYaw: 0,
        active: false,
        animFrameRequested: false, // 节流标志
        lastZoomTime: 0
      },
      // 移动端按以下规则分流触摸：落在摇杆区域内 → 控制移动；其余 → 控制视角
      tourTouchSegregationRadius: 130
    }
  },
  computed: {
    timerText () {
      const m = Math.floor(this.remainSec / 60)
      const s = this.remainSec % 60
      return pad(m) + ':' + pad(s)
    },
    currentPrivacyImg () {
      return this.privacySteps[this.privacyStep]
    },
    sandFilteredItems () {
      const list = SAND_CATALOG[this.sandActiveTab] || []
      const q = this.sandSearchQuery
      if (!q) return list
      return list.filter((entry) => entry.label.indexOf(q) !== -1)
    },
    propEditHudStyle () {
      if (!this.propEditHud || !this.propEditHud.active) return {}
      const isZhongguolong = this.propEditHud.propKind === 'dongwu:zhongguolong'
      return {
        left: (this.propEditHud.screenX + (isZhongguolong ? 30 : 0)) + 'px',
        top: (this.propEditHud.screenY + (isZhongguolong ? 40 : 0)) + 'px'
      }
    },
    tourHintText () {
      if (this.tourInWater) {
        return '水中行走 · 低头可查看水底 · WASD 移动 · Esc 退出'
      }
      return '移动鼠标环顾沙盘 · WASD 或方向键移动 · Esc 退出'
    },
    tourMobileHint () {
      if (!this.isMobileLayout()) return ''
      if (this.tourInWater) {
        return '拨动摇杆移动 · 拖动屏幕转视角 · 点击右上退出'
      }
      return '拨动摇杆移动 · 拖动屏幕外区域转视角 · 点击右上退出'
    },
    joystickKnobStyle () {
      const r = 50 // 最大像素半径，与 CSS 中 .tour-joystick 一致
      const dx = Math.max(-r, Math.min(r, this.tourJoystick.dx || 0))
      const dy = Math.max(-r, Math.min(r, this.tourJoystick.dy || 0))
      return {
        transform: 'translate(' + dx + 'px,' + dy + 'px)'
      }
    },
    joystickHintText () {
      const j = this.tourJoystick
      if (!j.activePointerId && !j.dx && !j.dy) return '拨动'
      const mag = Math.hypot(j.dx || 0, j.dy || 0)
      if (mag < 12) return '拨动'
      const ang = (Math.atan2(j.dy, j.dx) * 180) / Math.PI
      // ang: -180 = 左, -90 = 前(上), 0 = 右, 90 = 后(下)
      const norm = ((ang % 360) + 360) % 360
      if (norm > 67.5 && norm < 112.5) return '后退'
      if (norm > 247.5 && norm < 292.5) return '前进'
      if (norm >= 157.5 && norm <= 202.5) return '左移'
      if (norm >= 337.5 || norm <= 22.5) return '右移'
      if (norm > 22.5 && norm < 67.5) return '右后'
      if (norm > 112.5 && norm < 157.5) return '左后'
      if (norm > 202.5 && norm < 247.5) return '左前'
      return '右前'
    }
  },
  mounted () {
    this.saveRemaining = readSaveQuota()
    if (this.isMobileLayout()) {
      this.bindVisualViewport()
      this.checkLandscapeOrientation()
      window.addEventListener('orientationchange', this._onOrientationChange)
      window.addEventListener('resize', this._onOrientationChange)
      // 微信 X5 横屏需要 user gesture；监听首次触摸/点击时主动尝试一次
      // 注意：不能放在 mounted 同步路径里，否则 X5 会冷启动报 page reload
      this.bindAutoWeixinLandscape()
    }
    this.timerId = window.setInterval(() => {
      if (this.remainSec <= 0) return
      this.remainSec -= 1
      if (this.remainSec <= 0) this.onTimerExpired()
    }, 1000)
    this.initCamSliders()
    this.$nextTick(() => {
      if (this.isMobileLayout()) {
        this.applyMobileLayout()
        this.bindMobileTourLook()
        this.bindMobileSandPinch()
      }
    })
      // 隐私协议弹层（未确认过才弹）
    const session = getSandSession() || {}
    if (!session.privacyConfirmed) {
      this.privacyStep = 0
      this.showPrivacy = true
    }
    this._onPropSliderUnlock = () => {
      this.propEditHudSliderLock = false
      if (this.camOrbitSyncLock) {
        this.camOrbitSyncLock = false
        this.syncCamSlidersFromCamera()
      }
    }
    window.addEventListener('pointerup', this._onPropSliderUnlock)
    window.addEventListener('pointercancel', this._onPropSliderUnlock)
    this.tryRestoreSandTable()
  },
  beforeDestroy () {
    this.unbindVisualViewport()
    if (this.timerId) window.clearInterval(this.timerId)
    if (this._onPropSliderUnlock) {
      window.removeEventListener('pointerup', this._onPropSliderUnlock)
      window.removeEventListener('pointercancel', this._onPropSliderUnlock)
    }
    if (this._onOrientationChange) {
      window.removeEventListener('orientationchange', this._onOrientationChange)
      window.removeEventListener('resize', this._onOrientationChange)
    }
    this.unbindMobileTourLook()
    this.unbindMobileSandPinch()
    const pg = this.$refs.playground
    if (pg && typeof pg.exitTourMode === 'function') pg.exitTourMode()
  },
  methods: {
    isMobileLayout () {
      return (
        window.matchMedia('(max-width: 899px)').matches ||
        window.matchMedia('(max-height: 520px)').matches
      )
    },
    _onOrientationChange () {
      this.checkLandscapeOrientation()
    },
    checkLandscapeOrientation () {
      if (!this.isMobileLayout()) {
        this.forceLandscape = false
        return
      }
      const w = window.innerWidth
      const h = window.innerHeight
      const portrait = h > w
      if (portrait && !this.landscapePromptDismissed) {
        this.forceLandscape = true
      } else {
        this.forceLandscape = false
      }
    },
    dismissLandscapePrompt () {
      this.landscapePromptDismissed = true
      this.forceLandscape = false
    },
    tryLockLandscape () {
      // 微信 X5 内核：通过 WeixinJSBridge 强制横屏（最有效）
      const tryWeixinBridge = () => {
        // eslint-disable-next-line no-undef
        if (typeof WeixinJSBridge === 'undefined') return false
        // eslint-disable-next-line no-undef
        WeixinJSBridge.invoke('setOrientation', { orientation: 'landscape' })
        // 同时尝试进入全屏
        // eslint-disable-next-line no-undef
        if (WeixinJSBridge.invoke) {
          try {
            // eslint-disable-next-line no-undef
            WeixinJSBridge.invoke('setPageOrientation', { orientation: 'landscape' })
          } catch (_) {}
        }
        return true
      }
      // 如果微信 JSBridge 还没注入，监听注入事件后立即横屏
      if (typeof window.WeixinJSBridge === 'undefined') {
        document.addEventListener('WeixinJSBridgeReady', tryWeixinBridge, false)
      } else {
        tryWeixinBridge()
      }
      // Android Chrome: screen.orientation.lock() 需先进入全屏才生效
      const doLock = () => {
        const so = screen && screen.orientation
        if (so && typeof so.lock === 'function') {
          try {
            so.lock('landscape').catch(() => {})
          } catch (_) {}
        }
      }
      const el = document.documentElement
      if (el.requestFullscreen) {
        el.requestFullscreen().then(doLock).catch(doLock)
      } else if (el.webkitRequestFullScreen) {
        el.webkitRequestFullScreen()
        doLock()
      } else {
        doLock()
      }
    },
    bindAutoWeixinLandscape () {
      // 在用户首次交互（touchstart / click）时尝试一次横屏
      // X5 的 WeixinJSBridge 必须由用户手势触发才生效
      if (!this.isMobileLayout()) return
      if (this._wxAutoRotateBound) return
      this._wxAutoRotateBound = true
      const handler = () => {
        const w = window.innerWidth
        const h = window.innerHeight
        if (h > w) {
          // 只在 portrait 时尝试，避免横屏中再触发导致 reload
          this.tryLockLandscape()
        }
        window.removeEventListener('touchstart', handler, true)
        window.removeEventListener('click', handler, true)
        this._wxAutoRotateBound = false
      }
      window.addEventListener('touchstart', handler, true)
      window.addEventListener('click', handler, true)
    },
    applyMobileLayout () {
      const pg = this.$refs.playground
      if (pg && typeof pg.useMobileOrbitProfile === 'function') {
        pg.useMobileOrbitProfile()
        // 横竖屏切换 / 移动端 profile 应用时，把地面 yaw / scale 归位，让初始视角干净
        if (typeof pg.setSandboxYaw === 'function') {
          pg.setSandboxYaw(0)
        }
        if (typeof pg.setSandboxScale === 'function') {
          pg.setSandboxScale(1)
        }
        this.camYawDeg = 0
        // scale = 1 对应 camZoom 滑条值：1.0 = 0.5 + (v/100)*1.5 → v = 33
        this.camZoom = 33
        this.syncCamSlidersFromCamera()
      }
    },
    bindVisualViewport () {
      this._syncVisualViewport = () => {
        const vv = window.visualViewport
        const h = vv ? vv.height : window.innerHeight
        const w = vv ? vv.width : window.innerWidth
        const root = document.documentElement
        root.style.setProperty('--app-vh', h + 'px')
        root.style.setProperty('--app-vw', w + 'px')
        if (vv) {
          root.style.setProperty('--app-vv-offset-top', vv.offsetTop + 'px')
          root.style.setProperty('--app-vv-offset-left', vv.offsetLeft + 'px')
        } else {
          root.style.setProperty('--app-vv-offset-top', '0px')
          root.style.setProperty('--app-vv-offset-left', '0px')
        }
      }
      this._syncVisualViewport()
      if (window.visualViewport) {
        window.visualViewport.addEventListener('resize', this._syncVisualViewport)
        window.visualViewport.addEventListener('scroll', this._syncVisualViewport)
      }
      window.addEventListener('resize', this._syncVisualViewport)
      window.addEventListener('orientationchange', this._syncVisualViewport)
    },
    unbindVisualViewport () {
      if (this._syncVisualViewport && window.visualViewport) {
        window.visualViewport.removeEventListener('resize', this._syncVisualViewport)
        window.visualViewport.removeEventListener('scroll', this._syncVisualViewport)
      }
      if (this._syncVisualViewport) {
        window.removeEventListener('resize', this._syncVisualViewport)
        window.removeEventListener('orientationchange', this._syncVisualViewport)
      }
    },
    getSandItemKey: buildSandItemKey,
    waitPlaygroundReady () {
      return new Promise((resolve) => {
        const check = () => {
          const pg = this.$refs.playground
          if (pg && pg.sandboxRoot && pg._sandSculptGeo) {
            resolve(pg)
            return
          }
          requestAnimationFrame(check)
        }
        check()
      })
    },
    async tryRestoreSandTable () {
      const user = getUser()
      if (!user || !user.userId || this.restoreLoading) return

      const session = getSandSession() || {}
      if (session.restoreSand !== true) return

      const selectedClass = getSelectedClass() || {}
      const slot = String(
        session.slot || selectedClass.slot || getClassSlotKey(selectedClass, 0)
      )

      this.restoreLoading = true
      try {
        const res = await getSandTableData({
          userId: user.userId,
          slot
        })

        if (res.status === -100) {
          clearUser()
          message.warning('登录已过期，请重新登录')
          this.$router.replace({ name: 'Login' })
          return
        }

        if (res.status !== 0) return

        const saved = normalizeSandTableResponse(res)
        if (!hasSavedScene(saved)) return

        const pg = await this.waitPlaygroundReady()
        if (!pg || typeof pg.importSandTableScene !== 'function') return

        const meta = await pg.importSandTableScene(saved)
        if (meta) {
          if (meta.weather) this.activeWeatherId = meta.weather
          if (meta.terrainMaterial) this.activeMaterialId = meta.terrainMaterial
          if (meta.terrainVisible != null) this.terrainVisible = meta.terrainVisible
          if (meta.codingTime != null && meta.codingTime !== '') {
            const elapsed = parseInt(meta.codingTime, 10)
            if (!isNaN(elapsed)) {
              this.remainSec = Math.max(0, this.initialRemainSec - elapsed)
              if (this.remainSec <= 0) {
                this.$nextTick(() => this.onTimerExpired())
              }
            }
          }
        }
        message.success('已加载沙盘存档')
      } catch (err) {
        console.warn('[sand restore]', err)
      } finally {
        const session = getSandSession() || {}
        saveSandSession({ ...session, restoreSand: false })
        this.restoreLoading = false
      }
    },
    goBack () {
      this.finishPropEditIfAny(true)
      this.openSaveNameDialog()
    },
    openPrivacyGuide () {
      this.privacyStep = 0
      this.showPrivacy = true
    },
    onPrivacyNext () {
      if (this.privacyStep < this.privacySteps.length - 1) {
        this.privacyStep++
        // 第三步（说明3-地形工具）时展开地形工具
        if (this.privacyStep === 2) {
          this.terrainToolbarOpen = true
          this.terrainVisible = true
          this.activeTerrainTool = 'sculpt'
        }
        // 第四步（说明2-1-沙具选择与放置）时关闭地形工具并打开沙具面板
        if (this.privacyStep === 3) {
          this.terrainToolbarOpen = false
          this.terrainVisible = false
          this.activeTerrainTool = null
          this.sandPropsOpen = true
        }
        // 第五步时关闭沙具面板
        if (this.privacyStep === 4) {
          this.sandPropsOpen = false
        }
      } else {
        this.showPrivacy = false
        // 最后一步结束后收起地形工具
        this.terrainToolbarOpen = false
        this.terrainVisible = false
        this.activeTerrainTool = null
        const session = getSandSession() || {}
        saveSandSession({ ...session, privacyConfirmed: true })
      }
    },
    onTimerExpired () {
      if (this.timerExpired) return
      this.timerExpired = true
      this.finishPropEditIfAny(true)
      const pg = this.$refs.playground
      if (pg && typeof pg.exitTourMode === 'function') pg.exitTourMode()
      this.terrainToolbarOpen = false
      this.sandPropsOpen = false
      this.activeTerrainTool = null
      this.materialPaletteOpen = false
      this.clearSandSelection()
      this.openSaveNameDialog()
    },
    openSaveNameDialog () {
      this.saveRemaining = readSaveQuota()
      if (this.saveRemaining <= 0) {
        message.warning('今日保存次数已用完，明天再来吧')
        this.exitCancelConfirmOpen = true
        return
      }
      // 从 session 中读取用户年龄
      const session = getSandSession()
      this.userAge = session && session.age ? parseInt(session.age, 10) : 0
      this.saveNameOpen = true
    },
    onSaveNameCancel () {
      this.saveNameOpen = false
      // 退出存档 → 提示"确认取消保存将丢失所有操作记录"
      this.exitCancelConfirmOpen = true
    },
    reopenSaveNameDialog () {
      this.exitCancelConfirmOpen = false
      this.saveNameOpen = true
    },
    confirmDiscardAndLeave () {
      this.exitCancelConfirmOpen = false
      this.saveNameOpen = false
      this.$router.push({ name: 'WorldHome' })
    },
    async onSaveNameConfirm (workName, answers) {
      if (this.saving) return
      if (this.saveRemaining <= 0) {
        message.warning('今日保存次数已用完')
        return
      }
      // 18岁以下显示感谢语
      const showThanks = this.userAge > 0 && this.userAge < 18
      this.saving = true
      try {
        // 把 { q1: 0, q2: 2, ... } 转成 gadTable / phqTable
        const gadTable = []
        const phqTable = []
        for (const [qId, score] of Object.entries(answers)) {
          const num = parseInt(qId.replace('q', ''), 10) - 1
          const entry = { num, score }
          if (num <= 1) {
            gadTable.push(entry)
          } else {
            phqTable.push(entry)
          }
        }
        const pg = this.$refs.playground
        const sceneData =
          pg && typeof pg.exportSandTableScene === 'function'
            ? await pg.exportSandTableScene()
            : {}

        // 先截屏上传，获取缩略图 URL
        let screenshotUrl = ''
        const user = getUser() || {}
        const userId = user.userId || ''
        if (pg && typeof pg.captureScreenAsFile === 'function' && userId) {
          try {
            const file = await pg.captureScreenAsFile('sandplay_' + Date.now() + '.jpg')
            if (file) {
              const uploadRes = await uploadUserFile(file, userId)
              if (uploadRes && uploadRes.status === 0 && uploadRes.data) {
                screenshotUrl = uploadRes.data
              }
            }
          } catch (e) {
            console.warn('[upload screenshot]', e)
          }
        }

        // 将截图 URL 放入 sceneData（thumbnail、reviewPic、planform 都用同一个链接）
        if (screenshotUrl) {
          sceneData.thumbnail = screenshotUrl
          sceneData.reviewPic = screenshotUrl
          sceneData.planform = screenshotUrl
        }

        const elapsedSec = this.initialRemainSec - this.remainSec
        const session = getSandSession() || {}
        saveSandSession({ ...session, workName, answers })
        const payload = buildSandTablePayload({
          sceneData,
          activeWeatherId: this.activeWeatherId,
          elapsedSec,
          gadTable,
          phqTable
        })
        const res = await uploadSandTableData(payload)
        if (res.status !== 0) {
          message.error(res.msg || '保存失败')
          return
        }
        // 成功：扣减剩余次数（持久化）
        this.saveRemaining = Math.max(0, this.saveRemaining - 1)
        writeSaveQuota(this.saveRemaining)
        message.success('沙盘已保存')
        this.saveNameOpen = false

        if (showThanks) {
          // 18岁以下显示感谢语，延迟跳转
          this.saveSuccessMessage = '谢谢你今天和我一起玩这个游戏，我非常享受我们的时光。我非常期待下一次的冒险'
          this.saveSuccessOpen = true
          setTimeout(() => {
            this.saveSuccessOpen = false
            this.$router.push({ name: 'WorldHome' })
          }, 4000)
        } else {
          // 18岁及以上直接跳转
          this.$router.push({ name: 'WorldHome' })
        }
      } catch (err) {
        if (isUnauthorizedError(err)) return
        message.error(err.message || '保存失败，请稍后重试')
      } finally {
        this.saving = false
      }
    },
    toast (name) {
      message.info('「' + name + '」功能可在此接入业务逻辑')
    },
    toggleTour () {
      if (this.timerExpired) return
      const pg = this.$refs.playground
      if (!pg || this.tourTransitioning) return
      if (this.tourActive) {
        if (typeof pg.exitTourMode === 'function') pg.exitTourMode()
        return
      }
      this.finishPropEditIfAny(true)
      this.terrainToolbarOpen = false
      this.sandPropsOpen = false
      this.activeTerrainTool = null
      this.materialPaletteOpen = false
      this.terrainVisible = true
      this.clearSandSelection()
      if (typeof pg.enterTourMode === 'function') {
        pg.enterTourMode()
      }
    },
    onTourModeChange (payload) {
      const active =
        typeof payload === 'boolean' ? payload : !!(payload && payload.active)
      const transitioning =
        typeof payload === 'object' && payload && payload.transitioning
      const wasTransitioning = this.tourTransitioning
      this.tourActive = active
      this.tourTransitioning = !!transitioning
      if (active && !this.tourTransitioning && wasTransitioning) {
        message.info('已进入游览：贴地查看沙盘效果')
      }
      if (!active && !this.tourTransitioning) {
        this.tourInWater = false
        this.syncCamSlidersFromCamera()
      }
    },
    onTourWaterChange (payload) {
      this.tourInWater = !!(payload && payload.inWater)
    },
    setTourMove (dir, pressed) {
      const pg = this.$refs.playground
      if (pg && typeof pg.setTourMoveKey === 'function') {
        pg.setTourMoveKey(dir, pressed)
      }
    },
    /**
     * 移动端虚拟摇杆：根据当前偏移 (dx, dy) 决定 4 个方向键的按下/释放
     * - dx < -threshold → left
     * - dx >  threshold → right
     * - dy < -threshold → forward（屏幕坐标：y 向下为正，所以上滑是负值）
     * - dy >  threshold → back
     */
    _applyJoystickState (dx, dy) {
      const pg = this.$refs.playground
      if (!pg || typeof pg.setTourMoveKey !== 'function') return
      const T = 10
      const forward = dy < -T
      const back = dy > T
      const left = dx < -T
      const right = dx > T
      pg.setTourMoveKey('forward', forward)
      pg.setTourMoveKey('back', back)
      pg.setTourMoveKey('left', left)
      pg.setTourMoveKey('right', right)
    },
    _joystickCenter () {
      const el = this.$refs.tourJoystick
      if (!el) return { x: 0, y: 0 }
      const rect = el.getBoundingClientRect()
      return { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 }
    },
    onJoystickPointerDown (e) {
      if (!this.tourActive || this.tourTransitioning) return
      const center = this._joystickCenter()
      const j = this.tourJoystick
      j.activePointerId = e.pointerId
      j.dx = e.clientX - center.x
      j.dy = e.clientY - center.y
      this._applyJoystickState(j.dx, j.dy)
      try {
        e.target.setPointerCapture && e.target.setPointerCapture(e.pointerId)
      } catch (_) {}
    },
    onJoystickPointerMove (e) {
      const j = this.tourJoystick
      if (!j.activePointerId || e.pointerId !== j.activePointerId) return
      const center = this._joystickCenter()
      j.dx = e.clientX - center.x
      j.dy = e.clientY - center.y
      this._applyJoystickState(j.dx, j.dy)
    },
    onJoystickPointerUp (e) {
      const j = this.tourJoystick
      if (!j.activePointerId || (e && e.pointerId !== j.activePointerId)) {
        if (!e) {
          // 兜底：组件卸载等场景
        } else {
          return
        }
      }
      this._applyJoystickState(0, 0)
      j.activePointerId = null
      j.dx = 0
      j.dy = 0
    },
    /**
     * 移动端屏幕滑动调视角（在 playground 的 three canvas 上）。
     * 摇杆区域内的 pointer 已通过事件 stop.prevent 隔离，不会进入这里；
     * 其它屏幕区域 → 拖动改 yaw/pitch。
     */
    bindMobileTourLook () {
      if (!this.isMobileLayout()) return
      if (this._tourLookHandlers) return
      const onDown = (e) => {
        if (!this.tourActive || this.tourTransitioning) return
        // 排除来自顶部按钮 / 摇杆的指针
        if (e.target && e.target.closest && e.target.closest('.tour-joystick, .icon-btn, .pill-btn')) return
        // 双指缩放期间忽略单指 down
        if (this.pinchState && this.pinchState.suppressSingleFinger) return
        this.tourLookTouch.activePointerId = e.pointerId
        this.tourLookTouch.lastX = e.clientX
        this.tourLookTouch.lastY = e.clientY
        try {
          e.target.setPointerCapture && e.target.setPointerCapture(e.pointerId)
        } catch (_) {}
      }
      const onMove = (e) => {
        const t = this.tourLookTouch
        if (!t.activePointerId || e.pointerId !== t.activePointerId) return
        // 双指缩放期间禁用单指转视角
        if (this.pinchState && this.pinchState.suppressSingleFinger) return
        const dx = e.clientX - t.lastX
        const dy = e.clientY - t.lastY
        if (!dx && !dy) return
        t.lastX = e.clientX
        t.lastY = e.clientY
        const pg = this.$refs.playground
        if (pg && typeof pg.setTourLook === 'function') {
          pg.setTourLook(dx, dy)
        }
      }
      const onUp = (e) => {
        const t = this.tourLookTouch
        if (!t.activePointerId || (e && e.pointerId !== t.activePointerId)) return
        t.activePointerId = null
      }
      window.addEventListener('pointerdown', onDown)
      window.addEventListener('pointermove', onMove)
      window.addEventListener('pointerup', onUp)
      window.addEventListener('pointercancel', onUp)
      this._tourLookHandlers = { onDown, onMove, onUp }
    },
    unbindMobileTourLook () {
      if (!this._tourLookHandlers) return
      const { onDown, onMove, onUp } = this._tourLookHandlers
      window.removeEventListener('pointerdown', onDown)
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
      window.removeEventListener('pointercancel', onUp)
      this._tourLookHandlers = null
      this.tourLookTouch.activePointerId = null
    },
    /**
     * 移动端双指缩放 + 旋转地面。
     * 与单指 look / 摇杆的关系：
     * - 双指按下 → 在 pinchState 中记录两个 pointerId
     * - 双指 move → 计算两点距离变化 → 按比例调用 setSandboxScale
     *              + 计算两点连线角度变化 → 调用 setSandboxYawDelta 旋转地面
     * - 双指期间 suppressSingleFinger=true，让单指 look / 摇杆在 move 时跳过
     *   （摇杆已经通过 pointerId 校验自己的指针；look 同样）
     * - 双指抬起 → 清理 pointers；若还有 ≥1 个 pointer 不进入 pinch 模式
     */
    bindMobileSandPinch () {
      if (!this.isMobileLayout()) return
      if (this._pinchHandlers) return
      const isUI = (el) =>
        el && el.closest && el.closest('.tour-joystick, .icon-btn, .pill-btn, .cam-controls, .prop-edit-root, .prop-edit-pitch, .prop-edit-yaw, .prop-edit-height')

      // 用 requestAnimationFrame 节流，避免高频 pinch 计算
      const tickPinch = () => {
        const s = this.pinchState
        s.animFrameRequested = false
        if (!s.active || !s.firstTwoIds) return
        const pg = this.$refs.playground
        if (!pg) return
        const ids = s.firstTwoIds
        const dist = this._pinchDistance(s.pointers, ids)
        const angle = this._pinchAngle(s.pointers, ids)
        if (dist < 1 || s.baseDist < 1) return
        // scale 比例 = 当前距离 / 基准距离
        const scaleFactor = dist / s.baseDist
        if (typeof pg.getSandboxScale === 'function' && typeof pg.setSandboxScale === 'function') {
          const cur = pg.getSandboxScale()
          const base = s.baseSandboxScale || cur
          const lim = typeof pg.getSandboxScaleLimits === 'function' ? pg.getSandboxScaleLimits() : { min: 0.5, max: 2.0 }
          pg.setSandboxScale(Math.min(Math.max(base * scaleFactor, lim.min), lim.max))
        }
        // yaw 增量 = 当前角度 - 基准角度（度）
        if (typeof pg.setSandboxYawDelta === 'function') {
          let deltaDeg = ((angle - s.baseAngle) * 180) / Math.PI
          // 处理 atan2 的 ±π 跳变
          if (deltaDeg > 180) deltaDeg -= 360
          else if (deltaDeg < -180) deltaDeg += 360
          // 取反使滑动方向与旋转方向一致（向左滑逆时针，向右滑顺时针）
          const target = s.baseSandboxYaw - deltaDeg
          pg.setSandboxYaw(target)
        }
        s.lastZoomTime = performance.now()
      }

      const schedulePinch = () => {
        const s = this.pinchState
        if (s.animFrameRequested) return
        s.animFrameRequested = true
        const run = () => tickPinch()
        if (typeof requestAnimationFrame === 'function') {
          requestAnimationFrame(run)
        } else {
          setTimeout(run, 16)
        }
      }

      const onDown = (e) => {
        if (e.pointerType === 'mouse' && e.button !== 0) return
        // prop 编辑 HUD 显示时，禁用双指 pinch
        if (this.propEditHud && this.propEditHud.active) return
        if (isUI(e.target)) return
        const s = this.pinchState
        s.pointers[e.pointerId] = { x: e.clientX, y: e.clientY }
        const ids = Object.keys(s.pointers).map(Number)
        if (ids.length >= 2) {
          // 第二根手指落下：
          // 1) 阻止 canvas 接收此事件（避免 canvas 把它当 pan 起点）
          // 2) 调用 playground.cancelSandPan() 把当前 pan 模式静默清掉，
          //    不发伪造 pointerup（避免闪烁）
          if (e.target && e.target.tagName === 'CANVAS') {
            e.stopImmediatePropagation()
            e.stopPropagation()
            e.preventDefault()
          }
          const pg = this.$refs.playground
          if (pg && typeof pg.cancelSandPan === 'function') {
            pg.cancelSandPan()
          }
          // 锁定最早按下的两个手指 ID（防止后续多指抖动）
          s.firstTwoIds = [ids[0], ids[1]]
          const dist = this._pinchDistance(s.pointers, s.firstTwoIds)
          const angle = this._pinchAngle(s.pointers, s.firstTwoIds)
          s.baseDist = dist
          s.lastDist = dist
          s.baseAngle = angle
          s.lastAngle = angle
          s.initialized = true
          s.suppressSingleFinger = true
          s.active = true
          // 记下当时的 scale/yaw 作为基准
          if (pg) {
            s.baseSandboxScale = typeof pg.getSandboxScale === 'function' ? pg.getSandboxScale() : 1
            s.baseSandboxYaw = typeof pg.getSandboxYaw === 'function' ? pg.getSandboxYaw() : 0
          }
        }
      }

      const onMove = (e) => {
        const s = this.pinchState
        if (!(e.pointerId in s.pointers)) return
        s.pointers[e.pointerId] = { x: e.clientX, y: e.clientY }
        // 双指期间：吞掉 pointermove（pan 已 cancel），自己处理 pinch
        if (s.active && s.firstTwoIds && s.firstTwoIds.includes(e.pointerId)) {
          e.stopImmediatePropagation()
          e.stopPropagation()
          e.preventDefault()
          schedulePinch()
        }
      }

      const cleanup = (e) => {
        const s = this.pinchState
        if (e && e.pointerId in s.pointers) {
          delete s.pointers[e.pointerId]
        }
        // 只要任一参与 pinch 的手指离开，就退出 pinch 模式
        const remaining = Object.keys(s.pointers).map(Number)
        if (s.firstTwoIds && (!s.firstTwoIds.includes(e.pointerId) || remaining.length < 2)) {
          s.firstTwoIds = null
          s.baseDist = 0
          s.lastDist = 0
          s.baseAngle = 0
          s.lastAngle = 0
          s.suppressSingleFinger = false
          s.initialized = false
          s.active = false
          s.baseRadius = 3
          s.baseYaw = 0
          s.baseSandboxScale = 0
          s.baseSandboxYaw = 0
          s.animFrameRequested = false
          s.lastZoomTime = 0
        }
      }

      window.addEventListener('pointerdown', onDown, { capture: true })
      window.addEventListener('pointermove', onMove, { capture: true })
      window.addEventListener('pointerup', cleanup)
      window.addEventListener('pointercancel', cleanup)
      this._pinchHandlers = { onDown, onMove, cleanup }
    },
    _pinchDistance (pointers, ids) {
      if (ids.length < 2) return 0
      const a = pointers[ids[0]]
      const b = pointers[ids[1]]
      if (!a || !b) return 0
      return Math.hypot(a.x - b.x, a.y - b.y)
    },
    _pinchAngle (pointers, ids) {
      if (ids.length < 2) return 0
      const a = pointers[ids[0]]
      const b = pointers[ids[1]]
      if (!a || !b) return 0
      return Math.atan2(b.y - a.y, b.x - a.x)
    },
    unbindMobileSandPinch () {
      if (!this._pinchHandlers) return
      const { onDown, onMove, cleanup } = this._pinchHandlers
      window.removeEventListener('pointerdown', onDown, { capture: true })
      window.removeEventListener('pointermove', onMove, { capture: true })
      window.removeEventListener('pointerup', cleanup)
      window.removeEventListener('pointercancel', cleanup)
      this._pinchHandlers = null
      this.pinchState.pointers = Object.create(null)
      this.pinchState.firstTwoIds = null
      this.pinchState.lastDist = 0
      this.pinchState.lastAngle = 0
      this.pinchState.baseDist = 0
      this.pinchState.baseAngle = 0
      this.pinchState.suppressSingleFinger = false
      this.pinchState.active = false
      this.pinchState.baseRadius = 3
      this.pinchState.animFrameRequested = false
      this.pinchState.lastZoomTime = 0
      this._setPinchActive(false)
    },
    /** 双指操作时禁用 playground 的 pointer 事件，避免冲突 */
    _setPinchActive (active) {
      const pgEl = this.$refs.playground ? this.$refs.playground.$el : null
      if (pgEl) {
        pgEl.style.pointerEvents = active ? 'none' : ''
      }
    },
    toggleTerrainToolbar () {
      if (this.timerExpired) return
      this.terrainToolbarOpen = !this.terrainToolbarOpen
      if (this.terrainToolbarOpen) {
        this.terrainVisible = true
        this.sandPropsOpen = false
        this.clearSandSelection()
      }
      if (!this.terrainToolbarOpen) {
        this.activeTerrainTool = null
        this.materialPaletteOpen = false
      }
    },
    closeTerrainToolbar () {
      this.terrainToolbarOpen = false
      this.activeTerrainTool = null
      this.materialPaletteOpen = false
    },
    onTerrainToolbarBack () {
      if (this.materialPaletteOpen) {
        this.materialPaletteOpen = false
        return
      }
      this.closeTerrainToolbar()
    },
    clearSandSelection () {
      this.selectedSandItem = null
    },
    isSandItemSelected (item) {
      if (!this.selectedSandItem || !item) return false
      return this.selectedSandItem.key === buildSandItemKey(this.sandActiveTab, item)
    },
    selectTerrainTool (id) {
      this.finishPropEditIfAny(true)
      this.clearSandSelection()
      if (this.activeTerrainTool === id) {
        this.activeTerrainTool = null
        this.materialPaletteOpen = false
        return
      }
      if (id === 'material') {
        this.activeTerrainTool = 'material'
        this.materialPaletteOpen = true
        return
      }
      this.materialPaletteOpen = false
      this.activeTerrainTool = id
    },
    selectTerrainMaterial (id) {
      this.activeMaterialId = id
      this.activeTerrainTool = 'material'
    },
    /**
     * 沙盘地形交互：在选中地形工具时，左键在草台顶面拖动会触发。
     * payload: { tool, phase: 'down'|'move'|'up', point: { x, y, z } | null }
     * 可在此对接堆沙高度图、笔刷、保存等；右键仍为旋转视角。
     */
    onTerrainInteract (payload) {
      // 示例：if (payload.phase === 'move' && payload.tool === 'sculpt') { … }
      if (!payload) return
      if (payload.phase === 'up' && this.activeTerrainTool !== 'sculpt') {
        this.sculptHeightMeters = 0
      }
    },
    onTerrainSculptHeight (payload) {
      if (!payload) return
      const m = Number(payload.meters)
      if (Number.isFinite(m)) this.sculptHeightMeters = Math.max(0, Math.min(50, m))
    },
    prefetchSandTabThumbs (tab) {
      // 去掉全量预加载，避免一次性并发请求造成卡顿
      // 缩略图按需加载，loading="lazy" 会自动处理
    },
    toggleSandPropsPanel () {
      if (this.timerExpired) return
      this.sandPropsOpen = !this.sandPropsOpen
      if (this.sandPropsOpen) {
        this.terrainToolbarOpen = false
        this.activeTerrainTool = null
        this.materialPaletteOpen = false
        const pg = this.$refs.playground
        if (pg && typeof pg.clearPointerMode === 'function') pg.clearPointerMode()
        this.prefetchSandTabThumbs(this.sandActiveTab)
        preloadSandModels(SAND_MODEL_PRELOAD).catch(() => {})
      }
    },
    closeSandPropsPanel () {
      this.sandPropsOpen = false
    },
    /** 切换沙具分类（含天气）— Cursor Write It */
    selectSandTab (key) {
      this.sandActiveTab = key
      if (key === 'weather') {
        this.sandSearchInput = ''
        this.sandSearchQuery = ''
        this.clearSandSelection()
        return
      }
      this.sandSearchInput = ''
      this.sandSearchQuery = ''
      this.prefetchSandTabThumbs(key)
    },
    runSandSearch () {
      this.sandSearchQuery = this.sandSearchInput
    },
    clearSandSearch () {
      this.sandSearchInput = ''
      this.sandSearchQuery = ''
    },
    finishPropEditIfAny (commit = true) {
      const pg = this.$refs.playground
      if (pg && typeof pg.endPropEdit === 'function') pg.endPropEdit(commit)
    },
    selectSandItem (item) {
      if (this.timerExpired) return
      this.finishPropEditIfAny(true)
      const key = buildSandItemKey(this.sandActiveTab, item)
      if (this.selectedSandItem && this.selectedSandItem.key === key) {
        this.clearSandSelection()
        return
      }
      this.selectedSandItem = {
        key,
        label: item.label,
        emoji: item.emoji,
        modelKey: item.modelKey,
        modelUrl: item.modelUrl,
        thumbUrl: item.thumbUrl,
        previewUrl: item.previewUrl,
        category: this.sandActiveTab
      }
      this.terrainToolbarOpen = false
      this.activeTerrainTool = null
      this.materialPaletteOpen = false
      const pg = this.$refs.playground
      if (pg && typeof pg.clearPointerMode === 'function') pg.clearPointerMode()
      const modelRef = item.modelKey || item.modelUrl
      if (modelRef) prefetchSandModel(modelRef)
    },
    onSandPropPlaced () {
      this.clearSandSelection()
      this.closeSandPropsPanel()
      this.$nextTick(() => this.syncCamSlidersFromCamera())
    },
    onSandPropEdit (hud) {
      if (!hud || !hud.active) {
        this.propEditHud = null
        this.propEditBase = null
        this.propEditHudSliderLock = false
        return
      }
      const prev = this.propEditHud
      const entering = !prev || !prev.active || prev.id !== hud.id
      if (this.propEditHudSliderLock && prev) {
        this.propEditHud = {
          ...prev,
          active: true,
          id: hud.id,
          label: hud.label
        }
        return
      }
      if (entering) {
        this.propEditHud = { ...hud }
        this.clearSandSelection()
        this.syncPropEditSliders()
        this.$nextTick(() => this.syncCamSlidersFromCamera())
        return
      }
      this.propEditHud = { ...hud }
    },
    onPropEditSliderLock (locked, event) {
      this.propEditHudSliderLock = locked
      // 当按下滑条时，设置 pointer capture 确保触控事件被正确处理
      if (locked && event && event.target) {
        try {
          event.target.setPointerCapture(event.pointerId)
        } catch (err) {}
      }
    },
    /** 专门处理 touch 事件来设置 slider lock */
    onPropEditSliderTouch (locked, event) {
      if (locked) {
        this.propEditHudSliderLock = true
        // 对 touch 事件也设置 pointer capture
        if (event && event.target) {
          try {
            event.target.setPointerCapture(event.pointerId)
          } catch (err) {}
        }
      }
    },
    /** 滑条中点 50 为基准，偏移量 — Cursor Write It */
    propSliderCenterDelta (value) {
      return (value - 50) / 50
    },
    propSliderToPitchDelta (value) {
      const pg = this.$refs.playground
      const pitchMax =
        pg && typeof pg.getPropEditPitchMax === 'function'
          ? pg.getPropEditPitchMax()
          : PROP_PITCH_MAX
      // vertical-lr + direction:rtl 使 0 在下方（仰），需取反才与标签一致
      return -this.propSliderCenterDelta(value) * pitchMax
    },
    propSliderToYawDelta (value) {
      return this.propSliderCenterDelta(value) * PROP_YAW_DELTA_MAX
    },
    /** 高度滑条：50=进入编辑时高度，两端向上下限 — Cursor Write It */
    propHeightFromSlider (value) {
      if (!this.propEditBase) return 0
      const base = this.propEditBase.heightOffset
      const t = this.propSliderCenterDelta(value)
      if (t >= 0) return base + t * (PROP_HEIGHT_MAX - base)
      return base + t * (base - PROP_HEIGHT_MIN)
    },
    /** 进入编辑：以当前姿态为基准，滑条居中 — Cursor Write It */
    syncPropEditSliders () {
      const pg = this.$refs.playground
      if (!pg || typeof pg.getPropEditState !== 'function') return
      const st = pg.getPropEditState()
      if (!st) return
      this.propEditBase = {
        rotation: st.rotation,
        pitch: st.pitch || 0,
        heightOffset: st.heightOffset
      }
      this.propYaw = 50
      this.propPitch = 50
      this.propHeight = 50
    },
    onPropYawInput (e) {
      const v = Number(e.target.value)
      this.propYaw = v
      if (!this.propEditBase) return
      const pg = this.$refs.playground
      if (pg && typeof pg.setPropEditRotation === 'function') {
        pg.setPropEditRotation(
          this.propEditBase.rotation + this.propSliderToYawDelta(v)
        )
      }
    },
    onPropPitchInput (e) {
      const v = Number(e.target.value)
      this.propPitch = v
      if (!this.propEditBase) return
      const pg = this.$refs.playground
      if (pg && typeof pg.setPropEditPitch === 'function') {
        pg.setPropEditPitch(
          this.propEditBase.pitch + this.propSliderToPitchDelta(v)
        )
      }
    },
    onPropHeightInput (e) {
      const v = Number(e.target.value)
      this.propHeight = v
      if (!this.propEditBase) return
      const pg = this.$refs.playground
      if (pg && typeof pg.setPropEditHeight === 'function') {
        pg.setPropEditHeight(this.propHeightFromSlider(v))
      }
    },
    propEditAction (action) {
      const pg = this.$refs.playground
      if (pg && typeof pg.applyPropEditAction === 'function') {
        pg.applyPropEditAction(action)
      }
    },
    selectWeather (id) {
      if (this.timerExpired) return
      this.activeWeatherId = id
    },
    /** 同步俯仰/左右/远近滑条与当前相机状态 — Cursor Write It */
    initCamSliders () {
      this.$nextTick(() => {
        if (!this.syncCamSlidersFromCamera()) {
          window.setTimeout(() => this.syncCamSlidersFromCamera(), 150)
        }
      })
    },
    syncCamSlidersFromCamera () {
      if (this.camOrbitSyncLock) return false
      const pg = this.$refs.playground
      if (!pg) return false
      let ok = false

      if (typeof pg.getSandboxScale === 'function') {
        const scale = pg.getSandboxScale()
        const lim = pg.getSandboxScaleLimits ? pg.getSandboxScaleLimits() : { min: 0.5, max: 2.0 }
        const span = lim.max - lim.min
        if (span > 0) {
          const t = (scale - lim.min) / span
          this.camZoom = Math.round(t * 100)
          ok = true
        }
      }

      if (typeof pg.getCameraOrbitAngles === 'function') {
        const angles = pg.getCameraOrbitAngles()
        if (angles) {
          ok = true
          if (typeof pg.getCameraOrbitPitchLimits === 'function') {
            const lim = pg.getCameraOrbitPitchLimits()
            const span = lim.max - lim.min
            if (span > 0) {
              const t = (angles.pitchDeg - lim.min) / span
              this.camPitch = Math.round((1 - t) * 100)
            }
          }
        }
      }

      // yaw 已不再跟相机联动：滑条只管地面 (sandboxRoot) 旋转，
      // 这里不再用相机角度覆盖 this.camYawDeg，避免画面与滑条值不一致
      this.$nextTick(() => {
        const yawEl = this.$refs.camYawRange
        if (yawEl) yawEl.value = String(this.camYawDeg)
        const zoomEl = this.$refs.camZoomRange
        if (zoomEl) zoomEl.value = String(this.camZoom)
      })

      return ok
    },
    onCamControlPointerDown () {
      this.camOrbitSyncLock = true
    },
    onCamYawInput (e) {
      const v = Number(e.target.value)
      this.camYawDeg = v
      const pg = this.$refs.playground
      if (pg && typeof pg.setSandboxYaw === 'function') {
        pg.setSandboxYaw(v)
      }
    },
    onCamPitchInput (e) {
      const v = Number(e.target.value)
      this.camPitch = v
      const pg = this.$refs.playground
      if (!pg || typeof pg.setCameraOrbitPitch !== 'function') return
      const lim = pg.getCameraOrbitPitchLimits()
      const span = lim.max - lim.min
      if (span <= 0) return
      const t = v / 100
      const pitchDeg = lim.max - t * span
      pg.setCameraOrbitPitch(pitchDeg)
    },
    onCamZoomInput (e) {
      const v = Number(e.target.value)
      this.camZoom = v
      const pg = this.$refs.playground
      if (!pg || typeof pg.setSandboxScale !== 'function') return
      // 0~100 → 0.5~2.0 等比缩放地面（sandboxRoot）
      const lim = pg.getSandboxScaleLimits()
      const scale = lim.min + (v / 100) * (lim.max - lim.min)
      pg.setSandboxScale(scale)
    }
  }
}
</script>

<style scoped>
.island-play {
  --sand-panel-w: clamp(220px, 34vw, 300px);
  position: fixed;
  inset: 0;
  overflow: hidden;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'PingFang SC',
    'Microsoft YaHei', sans-serif;
}

.island-play--locked .island-play__three,
.island-play--locked .cam-controls,
.island-play--locked .island-play__bottom,
.island-play--locked .sand-sheet-root,
.island-play--locked .prop-edit-root,
.island-play--locked .top-actions,
.island-play--locked .island-play__hint {
  pointer-events: none;
  opacity: 0.55;
}

.island-play--locked .icon-btn {
  pointer-events: auto;
  opacity: 1;
}

.island-play__three {
  position: absolute;
  inset: 0;
  z-index: 0;
}

.island-play__hint {
  position: absolute;
  left: 50%;
  bottom: 112px;
  transform: translateX(-50%);
  z-index: 1;
  margin: 0;
  padding: 6px 14px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.88);
  color: #1565c0;
  font-size: 13px;
  font-weight: 600;
  pointer-events: none;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
}

/* 手机端视角滑条：左俯仰、上左右、右远近 — Cursor Write It */
.cam-controls {
  position: absolute;
  inset: 0;
  z-index: 2;
  pointer-events: none;
}

.cam-control {
  position: absolute;
  display: flex;
  align-items: center;
  gap: 6px;
  pointer-events: auto;
  touch-action: none;
  user-select: none;
  -webkit-user-select: none;
}

.cam-control--pitch {
  left: 8px;
  top: 50%;
  transform: translateY(-50%);
  flex-direction: column;
  padding: 4px 0;
}

.cam-control--yaw {
  left: 50%;
  top: 68px;
  transform: translateX(-50%);
  flex-direction: row;
  padding: 0 4px;
  max-width: min(72vw, 380px);
  width: min(72vw, 380px);
}

.cam-control--zoom {
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  flex-direction: column;
  padding: 4px 0;
  transition: right 0.28s ease;
}

.island-play--sand-open .cam-control--zoom {
  right: calc(var(--sand-panel-w) + 10px);
}

.island-play--sand-open .cam-control--yaw {
  max-width: min(58vw, 340px);
  width: min(58vw, 340px);
  left: calc((100% - var(--sand-panel-w)) * 0.5);
  transform: translateX(-50%);
}

.cam-control__tag {
  flex-shrink: 0;
  font-size: 12px;
  font-weight: 800;
  color: #0d47a1;
  text-shadow: 0 1px 0 rgba(255, 255, 255, 0.85);
  line-height: 1;
}

.cam-control__range {
  margin: 0;
  padding: 0;
  cursor: pointer;
  touch-action: none;
  -webkit-appearance: none;
  appearance: none;
  background: transparent;
  -webkit-tap-highlight-color: transparent;
}

.cam-control__range--h {
  flex: 1 1 auto;
  width: 100%;
  height: 28px;
}

.cam-control__vwrap {
  position: relative;
  width: 28px;
  height: min(34vh, 168px);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.cam-control__range--v {
  width: min(34vh, 168px);
  height: 28px;
  transform: rotate(-90deg);
  transform-origin: center;
  flex: 0 0 auto;
}

.cam-control__range::-webkit-slider-runnable-track {
  border-radius: 999px;
  background: linear-gradient(90deg, #64b5f6 0%, #1e88e5 100%);
  box-shadow: inset 0 1px 2px rgba(13, 71, 161, 0.25);
}

.cam-control__range--h::-webkit-slider-runnable-track {
  height: 8px;
}

.cam-control__range--v::-webkit-slider-runnable-track {
  height: 8px;
}

.cam-control__range::-webkit-slider-thumb {
  -webkit-appearance: none;
  width: 22px;
  height: 22px;
  margin-top: -7px;
  border-radius: 50%;
  border: 2px solid #fff;
  background: linear-gradient(180deg, #ffb74d 0%, #ff9800 55%, #f57c00 100%);
  box-shadow: 0 2px 8px rgba(230, 81, 0, 0.45);
}

.cam-control__range::-moz-range-track {
  border-radius: 999px;
  background: linear-gradient(90deg, #64b5f6 0%, #1e88e5 100%);
  border: none;
}

.cam-control__range--h::-moz-range-track {
  height: 8px;
}

.cam-control__range--v::-moz-range-track {
  height: 8px;
}

.cam-control__range::-moz-range-thumb {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: 2px solid #fff;
  background: linear-gradient(180deg, #ffb74d 0%, #ff9800 55%, #f57c00 100%);
  box-shadow: 0 2px 8px rgba(230, 81, 0, 0.45);
}

@media (max-width: 899px), (max-height: 520px) {
  .island-play {
    --sand-panel-w: clamp(248px, 68vw, 320px);
    --safe-top: env(safe-area-inset-top, 0px);
    --safe-right: env(safe-area-inset-right, 0px);
    --safe-bottom: env(safe-area-inset-bottom, 0px);
    --safe-left: env(safe-area-inset-left, 0px);
    top: var(--app-vv-offset-top, 0px);
    left: var(--app-vv-offset-left, 0px);
    width: var(--app-vw, 100vw);
    height: var(--app-vh, 100dvh);
    box-sizing: border-box;
    padding:
      var(--safe-top)
      var(--safe-right)
      var(--safe-bottom)
      var(--safe-left);
  }

  .cam-control--pitch {
    left: max(60px, var(--safe-left));
  }

  .cam-control--zoom {
    right: max(60px, var(--safe-right));
  }

  .cam-control--yaw {
    top: calc(56px + var(--safe-top));
  }

  .island-play__top {
    padding:
      max(10px, var(--safe-top))
      max(12px, var(--safe-right))
      8px
      max(12px, var(--safe-left));
  }

  .island-play__bottom {
    padding:
      12px
      max(14px, var(--safe-right))
      max(14px, var(--safe-bottom))
      max(14px, var(--safe-left));
  }

  .sand-sheet-root {
    top: var(--app-vv-offset-top, 0px);
    left: var(--app-vv-offset-left, 0px);
    right: auto;
    bottom: auto;
    width: var(--app-vw, 100vw);
    height: var(--app-vh, 100dvh);
    justify-content: center;
    align-items: center;
    padding:
      max(12px, var(--safe-top))
      max(12px, var(--safe-right))
      max(12px, var(--safe-bottom))
      max(12px, var(--safe-left));
    box-sizing: border-box;
  }

  .sand-sheet {
    width: min(500px, calc(100vw - 24px));
    flex: 0 0 auto;
    height: 89vh;
    max-height: 89vh;
    border-radius: 22px;
    border: 2px solid rgba(255, 255, 255, 0.92);
    box-shadow: 0 18px 48px rgba(0, 0, 0, 0.45);
    touch-action: pan-y;
  }

  .sand-sheet__head {
    padding: 10px 8px 8px;
    gap: 8px;
  }

  .sand-tab {
    padding: 7px 3px;
    font-size: 11px;
  }

  .sand-grid,
  .sand-weather-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
    grid-auto-rows: minmax(150px, auto);
    gap: 10px;
  }

  .sand-cell {
    padding: 8px 4px 10px;
    gap: 6px;
  }

  .sand-cell__thumb {
    min-height: 100px;
    font-size: 38px;
  }

  .sand-cell__thumb-emoji {
    font-size: 34px;
  }

  .sand-cell__label {
    font-size: 11px;
  }

  .sand-cell__model-tag {
    font-size: 9px;
    padding: 1px 5px;
  }
}

@media (orientation: landscape) and (max-width: 899px) and (max-height: 520px) {
  .cam-control--yaw {
    top: calc(36px + var(--safe-top));
    max-width: min(56vw, 300px);
    width: min(56vw, 300px);
  }

  .cam-control__range--v {
    height: min(24vh, 112px);
  }

  .island-play__top {
    padding-top: max(6px, var(--safe-top));
    padding-left: max(8px, var(--safe-left));
    padding-right: max(8px, var(--safe-right));
  }

  .island-play__bottom {
    padding:
      8px
      max(12px, var(--safe-right))
      max(10px, var(--safe-bottom))
      max(12px, var(--safe-left));
  }

  .island-play--sand-open .cam-control--yaw {
    max-width: min(44vw, 260px);
    width: min(44vw, 260px);
  }

  /* 横屏沙具面板：放宽宽度、改 2 列网格，避免窄 cell 把沙具图裁掉 */
  .island-play {
    --sand-panel-w: clamp(360px, 56vw, 460px);
  }
  .sand-sheet {
    border-radius: 14px 0 0 14px;
  }
  .sand-sheet__head {
    padding: 8px 8px 6px;
    gap: 6px;
  }
  .sand-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 10px;
    padding: 8px 8px 14px;
  }
  .sand-cell {
    padding: 6px 4px 8px;
  }
  .sand-cell__thumb {
    border-width: 1.5px;
  }
}

@media (min-width: 900px) {
  .cam-control--yaw {
    top: 25px;
  }

  .cam-control__range--v {
    height: min(28vh, 200px);
  }

  /* 顶部：定时器靠左，和移动端一致 */
  .island-play__top-line {
    gap: 8px;
    justify-content: flex-start;
  }

  .island-play__top-line .top-actions {
    margin-left: auto;
  }

  .timer {
    padding: 6px 12px;
    font-size: 14px;
    margin-left: 30px;
  }
}

/* 沙具编辑 HUD — Cursor Write It */
.prop-edit-root {
  --prop-ring-r: 98px;
  --prop-ring-d: calc(var(--prop-ring-r) * 0.70710678);
  --prop-slider-gap: 92px;
  --prop-side-slider-lift: 14px;
  /* 底部五钮共用圆弧半径（均匀分布 160°→20°）— Cursor Write It */
  --prop-btn-arc-r: 112px;
  --prop-track-h: linear-gradient(90deg, #ffcc80 0%, #ff9800 52%, #ef6c00 100%);
  --prop-track-v: linear-gradient(180deg, #ffcc80 0%, #ff9800 52%, #ef6c00 100%);
  --prop-btn-bg: linear-gradient(180deg, #ffcc80 0%, #ff9800 52%, #ef6c00 100%);
  position: fixed;
  z-index: 12;
  width: 0;
  height: 0;
  pointer-events: none;
  transform: translate3d(-50%, -50%, 0);
  backface-visibility: hidden;
}

/* 中心留给 3D 模型，不叠加 UI 圈 — Cursor Write It */
.prop-edit-focus {
  display: none;
}

/* 参考稿布局（仅 CSS）：上左右、左高低、右俯仰 — Cursor Write It */
.prop-edit-yaw {
  position: absolute;
  left: 50%;
  top: 50%;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  width: min(52vw, 200px);
  transform: translate(-50%, calc(-50% - var(--prop-slider-gap)));
  pointer-events: auto;
  touch-action: none;
  user-select: none;
  -webkit-user-select: none;
  z-index: 1;
}

.prop-edit-height {
  position: absolute;
  left: 50%;
  top: 50%;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  padding: 4px 0;
  transform: translate(
    calc(-50% - var(--prop-slider-gap)),
    calc(-50% - var(--prop-side-slider-lift))
  );
  pointer-events: auto;
  touch-action: none;
  user-select: none;
  -webkit-user-select: none;
  z-index: 1;
}

.prop-edit-pitch {
  position: absolute;
  left: 50%;
  top: 50%;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  padding: 4px 0;
  transform: translate(
    calc(-50% + var(--prop-slider-gap)),
    calc(-50% - var(--prop-side-slider-lift))
  );
  pointer-events: auto;
  touch-action: none;
  user-select: none;
  -webkit-user-select: none;
  z-index: 1;
}

.prop-edit-pitch__tag,
.prop-edit-yaw__tag,
.prop-edit-height__tag {
  flex-shrink: 0;
  font-size: 12px;
  font-weight: 700;
  color: #e65100;
  line-height: 1;
  letter-spacing: 0.04em;
}

.prop-edit-pitch__range,
.prop-edit-yaw__range,
.prop-edit-height__range {
  -webkit-tap-highlight-color: transparent;
}

.prop-edit-yaw__range {
  flex: 1 1 auto;
  width: 100%;
  height: 28px;
  margin: 0;
  padding: 0;
  cursor: pointer;
  touch-action: none;
  -webkit-appearance: none;
  appearance: none;
  background: transparent;
}

.prop-edit-pitch__range,
.prop-edit-height__range {
  width: 28px;
  height: min(22vh, 120px);
  margin: 0;
  padding: 0;
  cursor: pointer;
  touch-action: none;
  -webkit-appearance: none;
  appearance: none;
  background: transparent;
  writing-mode: vertical-lr;
  direction: rtl;
}

.prop-edit-yaw__range::-webkit-slider-runnable-track {
  height: 8px;
  border-radius: 999px;
  background: var(--prop-track-h);
  box-shadow: inset 0 1px 2px rgba(191, 54, 12, 0.28);
}

.prop-edit-pitch__range::-webkit-slider-runnable-track,
.prop-edit-height__range::-webkit-slider-runnable-track {
  width: 8px;
  border-radius: 999px;
  background: var(--prop-track-v);
  box-shadow: inset 0 1px 2px rgba(191, 54, 12, 0.28);
}

.prop-edit-yaw__range::-webkit-slider-thumb {
  -webkit-appearance: none;
  width: 22px;
  height: 22px;
  margin-top: -7px;
  border-radius: 50%;
  border: 2px solid rgba(255, 255, 255, 0.95);
  background: #fff;
  box-shadow: 0 2px 8px rgba(191, 54, 12, 0.35);
}

.prop-edit-pitch__range::-webkit-slider-thumb,
.prop-edit-height__range::-webkit-slider-thumb {
  -webkit-appearance: none;
  width: 22px;
  height: 22px;
  margin-left: -7px;
  border-radius: 50%;
  border: 2px solid rgba(255, 255, 255, 0.95);
  background: #fff;
  box-shadow: 0 2px 8px rgba(191, 54, 12, 0.35);
}

.prop-edit-yaw__range::-moz-range-track {
  height: 8px;
  border-radius: 999px;
  background: var(--prop-track-h);
  border: none;
}

.prop-edit-pitch__range::-moz-range-track,
.prop-edit-height__range::-moz-range-track {
  width: 8px;
  border-radius: 999px;
  background: var(--prop-track-v);
  border: none;
}

.prop-edit-yaw__range::-moz-range-thumb,
.prop-edit-pitch__range::-moz-range-thumb,
.prop-edit-height__range::-moz-range-thumb {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: 2px solid rgba(255, 255, 255, 0.95);
  background: #fff;
  box-shadow: 0 2px 8px rgba(191, 54, 12, 0.35);
}

.prop-edit-btn {
  position: absolute;
  left: 50%;
  top: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  margin: -21px 0 0 -21px;
  padding: 0;
  border: none;
  border-radius: 50%;
  font-size: 22px;
  font-weight: 800;
  line-height: 1;
  cursor: pointer;
  pointer-events: auto;
  box-shadow: 0 3px 10px rgba(191, 54, 12, 0.28);
  background: var(--prop-btn-bg);
  color: #fff;
  transform: translate(
    calc(-50% + var(--prop-x, 0px)),
    calc(-50% + var(--prop-y, 0px))
  );
  transition: filter 0.12s ease, box-shadow 0.12s ease;
}

.prop-edit-btn:active {
  filter: brightness(0.96);
}

/* 下弧五钮均匀分布：+ 取消 删除 确认 − — Cursor Write It */
.prop-edit-btn--scale-up,
.prop-edit-btn--scale-down,
.prop-edit-btn--cancel,
.prop-edit-btn--delete,
.prop-edit-btn--confirm {
  background: var(--prop-btn-bg);
  color: #fff;
}

.prop-edit-btn--scale-up {
  --prop-x: calc(-1 * var(--prop-btn-arc-r) * 0.94);
  --prop-y: calc(var(--prop-btn-arc-r) * 0.74);
  font-size: 28px;
}

.prop-edit-btn--cancel {
  --prop-x: calc(-1 * var(--prop-btn-arc-r) * 0.57);
  --prop-y: calc(var(--prop-btn-arc-r) * 0.82);
}

.prop-edit-btn--delete {
  --prop-x: 0px;
  --prop-y: var(--prop-btn-arc-r);
}

.prop-edit-btn--confirm {
  --prop-x: calc(var(--prop-btn-arc-r) * 0.57);
  --prop-y: calc(var(--prop-btn-arc-r) * 0.82);
}

.prop-edit-btn--scale-down {
  --prop-x: calc(var(--prop-btn-arc-r) * 1.27);
  --prop-y: calc(var(--prop-btn-arc-r) * 0.74);
  font-size: 30px;
}

.prop-edit-btn--label {
  width: auto;
  min-width: 50px;
  height: 40px;
  margin: -12px 0 0 -6px;
  padding: 0 10px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 800;
  letter-spacing: 0.04em;
}

/* 手机端沙具：右侧固定面板 — Cursor Write It */
.sand-sheet-root {
  position: fixed;
  inset: 0;
  z-index: 5;
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  align-items: stretch;
  pointer-events: none;
}

.sand-sheet {
  pointer-events: auto;
  position: relative;
  flex: 0 0 var(--sand-panel-w);
  width: var(--sand-panel-w);
  height: 100%;
  margin: 0;
  border-radius: 18px 0 0 18px;
  border: 2px solid rgba(255, 255, 255, 0.92);
  background: linear-gradient(180deg, #0d47a1 0%, #1565c0 38%, #1976d2 100%);
  box-shadow: -6px 0 28px rgba(13, 71, 161, 0.45),
    inset 0 1px 0 rgba(255, 255, 255, 0.12);
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
}

.sand-sheet__collapse {
  position: absolute;
  left: 0;
  top: 35%;
  transform: translateY(-50%);
  width: 30px;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.15);
  border: none;
  border-radius: 0 12px 12px 0;
  color: #fff;
  cursor: pointer;
  pointer-events: auto;
  transition: background 0.15s ease;
  z-index: 1;
  padding: 0;
}

.sand-sheet__collapse:hover {
  background: rgba(255, 255, 255, 0.25);
}

.sand-sheet__collapse svg {
  display: block;
}

/* PC 端收起按钮位置 */
@media (min-width: 900px) and (min-height: 521px) {
  .sand-sheet__collapse {
    top: 15%;
  }
}

.sand-sheet__head {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 12px 10px 10px 40px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.12);
  flex-shrink: 0;
}

.sand-sheet__build-tag {
  margin: 0;
  padding: 4px 8px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 600;
  color: #fff8e1;
  background: rgba(255, 152, 0, 0.55);
  text-align: center;
}

.sand-tabs {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 6px;
  width: 100%;
  min-width: 0;
}

.sand-tab {
  padding: 8px 4px;
  border: none;
  border-radius: 8px;
  background: rgba(144, 202, 249, 0.35);
  color: rgba(255, 255, 255, 0.95);
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  white-space: nowrap;
  text-align: center;
  line-height: 1.2;
}

.sand-tab:hover {
  background: rgba(255, 255, 255, 0.18);
}

.sand-tab--on {
  background: rgba(13, 71, 161, 0.55);
  box-shadow: inset 0 0 0 2px rgba(255, 202, 40, 0.75);
  color: #fff;
}

.sand-sheet__search {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  min-width: 0;
  padding: 6px 8px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.96);
  box-sizing: border-box;
}

.sand-sheet__search-input {
  flex: 1 1 auto;
  min-width: 0;
  padding: 4px 0;
  border: none;
  background: transparent;
  color: #0d47a1;
  font-size: 14px;
  font-weight: 600;
}

.sand-sheet__search-input::placeholder {
  color: #78909c;
}

.sand-sheet__search-input:focus {
  outline: none;
}

.sand-sheet__search-go {
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: #e3f2fd;
  color: #1565c0;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.sand-sheet__search-go:hover {
  background: #bbdefb;
}

.sand-sheet__search-clear {
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: #90a4ae;
  color: #fff;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.sand-sheet__search-clear:hover {
  background: #607d8b;
}

.sand-sheet__grid-wrap {
  position: relative;
  flex: 1 1 0;
  min-height: 0;
  overflow-x: hidden;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  touch-action: pan-y;
  overscroll-behavior-y: contain;
  display: flex;
  flex-direction: column;
}

.sand-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 8px;
  padding: 10px 8px 16px;
  flex: 0 0 auto;
  align-content: start;
}

.sand-cell {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding: 8px 4px 10px;
  border: none;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.1);
  color: #fff;
  cursor: pointer;
  transition: background 0.15s ease, box-shadow 0.15s ease;
}

.sand-cell:hover {
  background: rgba(255, 255, 255, 0.18);
  box-shadow: 0 0 0 2px rgba(255, 202, 40, 0.55);
}

.sand-cell--on {
  background: rgba(255, 248, 225, 0.28);
  box-shadow: 0 0 0 2px #ffca28;
}

.sand-cell__thumb {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  aspect-ratio: 1;
  max-width: 100%;
  border-radius: 10px;
  background: linear-gradient(145deg, #e3f2fd 0%, #90caf9 40%, #42a5f5 100%);
  border: 2px solid rgba(255, 255, 255, 0.55);
  box-sizing: border-box;
  font-size: 30px;
  line-height: 1;
  overflow: hidden;
}

.sand-cell__thumb-img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  object-position: center center;
  display: block;
}

.sand-cell__thumb-emoji {
  font-size: 26px;
  line-height: 1;
}

.sand-cell__model-tag {
  position: absolute;
  right: 3px;
  bottom: 3px;
  padding: 1px 5px;
  border-radius: 6px;
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.02em;
  color: #fff;
  background: rgba(13, 71, 161, 0.82);
  line-height: 1.2;
}

.sand-cell__label {
  font-size: 10px;
  font-weight: 700;
  line-height: 1.2;
  text-align: center;
  width: 100%;
  word-break: break-all;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.sand-sheet__empty {
  margin: 0;
  padding: 24px 16px;
  text-align: center;
  color: rgba(255, 255, 255, 0.75);
  font-size: 14px;
}

/* 沙具面板内天气选项 — Cursor Write It */
.sand-weather-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 8px;
  padding: 10px 8px 16px;
  flex: 0 0 auto;
  align-content: start;
}

.sand-weather-cell {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding: 10px 6px 12px;
  border: none;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.1);
  color: #fff;
  cursor: pointer;
  transition: background 0.15s ease, box-shadow 0.15s ease;
}

.sand-weather-cell:hover {
  background: rgba(255, 255, 255, 0.18);
  box-shadow: 0 0 0 2px rgba(255, 202, 40, 0.55);
}

.sand-weather-cell--on {
  background: rgba(255, 248, 225, 0.28);
  box-shadow: 0 0 0 2px #ffca28;
}

.sand-weather-cell--close {
  background: rgba(255, 100, 100, 0.25);
  color: #ff9999;
}

.sand-weather-cell--close:hover {
  background: rgba(255, 100, 100, 0.4);
  box-shadow: 0 0 0 2px rgba(255, 100, 100, 0.6);
}

.sand-weather-cell__ic {
  font-size: 28px;
  line-height: 1;
}

.sand-weather-cell__label {
  font-size: 10px;
  font-weight: 700;
  line-height: 1.25;
  text-align: center;
  width: 100%;
  word-break: break-all;
}

.sand-sheet-enter-active,
.sand-sheet-leave-active {
  transition: opacity 0.28s ease;
}

.sand-sheet-enter-active .sand-sheet,
.sand-sheet-leave-active .sand-sheet {
  transition: transform 0.32s cubic-bezier(0.4, 0, 0.2, 1);
}

.sand-sheet-enter,
.sand-sheet-leave-to {
  opacity: 0;
}

.sand-sheet-enter .sand-sheet,
.sand-sheet-leave-to .sand-sheet {
  transform: translateX(100%);
}

.island-play--sand-open .island-play__hint {
  left: 38%;
  max-width: min(56vw, 320px);
}

@media (min-width: 900px) {
  .island-play {
    --sand-panel-w: 280px;
  }

  .sand-sheet-root {
    top: 10px;
    bottom: 88px;
    height: 98dvh;
  }

  .sand-sheet {
    height: 100%;
    border-radius: 18px;
  }
}

.island-play__top {
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  z-index: 4;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 8px;
  padding: 12px 14px;
  pointer-events: none;
}

.island-play__top-line {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  pointer-events: none;
}

.island-play__top-line > * {
  pointer-events: auto;
}

.icon-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border: none;
  border-radius: 10px;
  background: #1976d2;
  color: #fff;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(13, 71, 161, 0.35);
}

.icon-btn:hover {
  background: #1565c0;
}

.timer {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.92);
  color: #0d47a1;
  font-weight: 700;
  font-size: 17px;
  letter-spacing: 0.04em;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.12);
}

.timer__icon {
  font-size: 18px;
  line-height: 1;
}

.timer--expired {
  background: #ffebee;
  color: #c62828;
}

.top-actions {
  display: flex;
  gap: 8px;
}

.pill-btn {
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-width: 52px;
  padding: 6px 8px 8px;
  border: none;
  border-radius: 10px;
  background: #1976d2;
  color: #fff;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(13, 71, 161, 0.3);
  line-height: 1.2;
}

.pill-btn:hover {
  background: #1565c0;
}

.pill-btn__ic {
  font-size: 16px;
  margin-bottom: 2px;
}

.pill-btn--active {
  background: #0d47a1;
  box-shadow: inset 0 2px 6px rgba(0, 0, 0, 0.22);
}

.tour-bar {
  position: absolute;
  left: 50%;
  bottom: 24px;
  transform: translateX(-50%);
  z-index: 6;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  pointer-events: none;
}

.tour-bar__hint {
  margin: 0;
  padding: 8px 14px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.92);
  color: #0d47a1;
  font-size: 12px;
  font-weight: 600;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.12);
  white-space: nowrap;
}

.island-play--tour .tour-bar__hint {
  animation: tour-hint-pulse 1.6s ease-in-out infinite;
}

.island-play--tour:not(.island-play--tour-ready) .tour-bar__hint {
  animation: none;
}

@keyframes tour-hint-pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.72;
  }
}

.pill-btn:disabled {
  opacity: 0.65;
  cursor: wait;
}

.tour-pad {
  position: relative;
  width: 132px;
  height: 132px;
  pointer-events: auto;
}

.tour-pad__btn {
  position: absolute;
  width: 44px;
  height: 44px;
  border: none;
  border-radius: 50%;
  background: linear-gradient(180deg, #42a5f5 0%, #1565c0 100%);
  color: #fff;
  font-size: 18px;
  font-weight: 800;
  line-height: 1;
  cursor: pointer;
  box-shadow: 0 3px 10px rgba(13, 71, 161, 0.35);
  touch-action: none;
  user-select: none;
  -webkit-user-select: none;
}

.tour-pad__btn:active {
  filter: brightness(0.94);
}

.tour-pad__btn--fwd {
  left: 50%;
  top: 0;
  margin-left: -22px;
}

.tour-pad__btn--back {
  left: 50%;
  bottom: 0;
  margin-left: -22px;
}

.tour-pad__btn--left {
  left: 0;
  top: 50%;
  margin-top: -22px;
}

.tour-pad__btn--right {
  right: 0;
  top: 50%;
  margin-top: -22px;
}

@media (min-width: 900px) {
  .tour-pad {
    display: none;
  }
}

.tour-joystick {
  position: relative;
  width: 160px;
  height: 160px;
  pointer-events: auto;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.15);
  border: 2px solid rgba(255, 255, 255, 0.5);
  box-shadow: 0 4px 16px rgba(13, 71, 161, 0.25);
  touch-action: none;
  user-select: none;
  -webkit-user-select: none;
}

.tour-joystick__knob {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: linear-gradient(180deg, #42a5f5 0%, #1565c0 100%);
  box-shadow: 0 4px 12px rgba(13, 71, 161, 0.55);
  transition: transform 0.05s linear;
  will-change: transform;
}

.tour-joystick__hint {
  position: absolute;
  bottom: -22px;
  left: 50%;
  transform: translateX(-50%);
  font-size: 11px;
  color: rgba(13, 71, 161, 0.85);
  background: rgba(255, 255, 255, 0.85);
  padding: 2px 8px;
  border-radius: 999px;
  pointer-events: none;
  white-space: nowrap;
}

@media (max-width: 899px), (max-height: 520px) {
  .tour-joystick {
    display: flex;
    align-self: flex-start;
    margin-left: 10px;
  }
  .tour-bar {
    left: 0;
    transform: none;
    align-items: flex-start;
  }
}

.island-play__bottom {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 4;
  padding: 16px 18px 22px;
  pointer-events: none;
}

.island-play__dock-row {
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: 12px;
  pointer-events: none;
}

/* 与「地形」大按钮左下角同一锚点：收起为一块按钮，展开为横向工具条 */
.terrain-dock {
  flex: 0 1 auto;
  align-self: flex-end;
  max-width: 98px;
  overflow: visible;
  border-radius: 14px;
  pointer-events: auto;
  transition: max-width 0.34s cubic-bezier(0.4, 0, 0.2, 1),
    box-shadow 0.25s ease;
  will-change: max-width;
}

.terrain-dock--open {
  max-width: calc(100vw - 96px);
  box-shadow: 0 0 0 3px #ffca28, 0 8px 22px rgba(13, 71, 161, 0.35);
}

.terrain-dock--material {
  max-width: calc(100vw - 80px);
}

.terrain-dock__surface {
  display: inline-flex;
  width: max-content;
  align-items: flex-end;
}

.dock-btn--sand {
  flex-shrink: 0;
}

.terrain-toolbar {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px 8px 8px;
  min-height: 88px;
  box-sizing: border-box;
  border-radius: 14px;
  border: 2px solid rgba(255, 255, 255, 0.95);
  background: linear-gradient(180deg, #0d47a1 0%, #1565c0 42%, #1e88e5 100%);
  box-shadow: 0 8px 22px rgba(13, 71, 161, 0.45),
    inset 0 1px 0 rgba(255, 255, 255, 0.14);
  flex-shrink: 0;
  max-width: 100%;
  overflow-x: auto;
  overflow-y: hidden;
  scrollbar-width: thin;
  scrollbar-color: rgba(255, 255, 255, 0.55) rgba(0, 0, 0, 0.15);
}

.terrain-toolbar::-webkit-scrollbar {
  height: 6px;
}

.terrain-toolbar::-webkit-scrollbar-track {
  background: rgba(0, 0, 0, 0.18);
  border-radius: 3px;
}

.terrain-toolbar::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.6);
  border-radius: 3px;
}

.terrain-toolbar__tools {
  display: flex;
  align-items: stretch;
  gap: 6px;
  flex-wrap: nowrap;
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
  max-width: 100%;
  scrollbar-width: thin;
  scrollbar-color: rgba(255, 255, 255, 0.55) rgba(0, 0, 0, 0.15);
  padding-bottom: 4px;
}

.terrain-toolbar__tools::-webkit-scrollbar {
  height: 6px;
  display: block;
}

.terrain-toolbar__tools::-webkit-scrollbar-track {
  background: rgba(0, 0, 0, 0.18);
  border-radius: 3px;
}

.terrain-toolbar__tools::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.6);
  border-radius: 3px;
}

.terrain-toolbar__back {
  flex-shrink: 0;
  width: 44px;
  height: 44px;
  padding: 0;
  border-radius: 50%;
  border: 2px solid rgba(255, 255, 255, 0.92);
  background: rgba(255, 255, 255, 0.14);
  color: #fff;
  pointer-events: auto;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s ease;
}

.terrain-toolbar__back:hover {
  background: rgba(255, 255, 255, 0.24);
}

.terrain-tool {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  padding: 4px 4px 6px;
  min-width: 56px;
  border: none;
  background: transparent;
  cursor: pointer;
  border-radius: 10px;
  color: #fff;
  pointer-events: auto;
  transition: background 0.15s ease, box-shadow 0.15s ease;
  flex-shrink: 0;
}

.terrain-tool:hover {
  background: rgba(255, 255, 255, 0.1);
}

.terrain-tool--on {
  background: rgba(255, 202, 40, 0.2);
  box-shadow: inset 0 0 0 3px #ffca28, 0 0 14px rgba(255, 202, 40, 0.5);
}

.terrain-tool__icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 50px;
  height: 50px;
  border-radius: 10px;
  border: 2px solid rgba(255, 255, 255, 0.92);
  background: rgba(255, 255, 255, 0.1);
  box-sizing: border-box;
}

.terrain-tool__icon svg {
  display: block;
  width: 36px;
  height: 36px;
}

.terrain-tool__label {
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.02em;
  line-height: 1.15;
}

.terrain-toolbar--material-open .terrain-toolbar__tools {
  align-items: center;
}

.terrain-material-palette {
  display: flex;
  align-items: flex-end;
  gap: 8px;
  padding-left: 4px;
  flex-shrink: 0;
}

.terrain-material {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 5px;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  flex-shrink: 0;
  color: #fff;
  pointer-events: auto;
}

.terrain-material__swatch {
  display: block;
  width: 52px;
  height: 52px;
  border-radius: 50%;
  border: 2px solid rgba(255, 255, 255, 0.95);
  box-sizing: border-box;
  box-shadow: inset 0 2px 6px rgba(0, 0, 0, 0.12);
}

.terrain-material--on .terrain-material__swatch {
  box-shadow:
    0 0 0 2px #ffca28,
    inset 0 2px 6px rgba(0, 0, 0, 0.1);
}

.terrain-material__label {
  font-size: 12px;
  font-weight: 700;
  line-height: 1.1;
  white-space: nowrap;
}

.terrain-material__swatch--grass {
  background:
    radial-gradient(circle at 30% 35%, #7cb342 0%, #7cb342 12%, transparent 13%),
    radial-gradient(circle at 62% 58%, #558b2f 0%, #558b2f 10%, transparent 11%),
    radial-gradient(circle at 48% 72%, #aed581 0%, #aed581 8%, transparent 9%),
    linear-gradient(160deg, #66bb6a 0%, #2e7d32 100%);
}

.terrain-material__swatch--sand {
  background:
    repeating-linear-gradient(
      105deg,
      transparent 0 6px,
      rgba(188, 143, 90, 0.35) 6px 10px
    ),
    linear-gradient(180deg, #e8c98a 0%, #c9a66b 45%, #b08952 100%);
}

.terrain-material__swatch--snow {
  background:
    radial-gradient(circle at 40% 35%, #ffffff 0%, #ffffff 18%, transparent 19%),
    radial-gradient(circle at 70% 65%, #f5f5f5 0%, #f5f5f5 14%, transparent 15%),
    linear-gradient(180deg, #fafafa 0%, #e0e0e0 100%);
}

.terrain-material__swatch--brick {
  background:
    linear-gradient(180deg, rgba(0, 0, 0, 0.08) 50%, transparent 50%),
    linear-gradient(90deg, rgba(0, 0, 0, 0.1) 50%, transparent 50%),
    linear-gradient(180deg, #6d4c41 0%, #5d4037 100%);
  background-size: 100% 14px, 22px 100%, 100% 100%;
}

.terrain-material__swatch--rock {
  background:
    radial-gradient(circle at 25% 30%, #9e9e9e 0%, #757575 35%, transparent 36%),
    radial-gradient(circle at 70% 55%, #616161 0%, #424242 40%, transparent 41%),
    linear-gradient(145deg, #bdbdbd 0%, #616161 100%);
}

.dock-btn {
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-width: 88px;
  min-height: 88px;
  padding: 10px 12px;
  border: 3px solid rgba(255, 255, 255, 0.35);
  border-radius: 14px;
  background: linear-gradient(165deg, #1e88e5 0%, #1565c0 100%);
  pointer-events: auto;
  color: #fff;
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 8px 22px rgba(13, 71, 161, 0.45);
}

.dock-btn:hover {
  filter: brightness(1.06);
}

.dock-btn--sand-on {
  box-shadow: 0 0 0 3px #ffca28, 0 8px 22px rgba(13, 71, 161, 0.45);
}

.dock-btn--terrain {
  flex-shrink: 0;
}

.dock-btn__ic {
  font-size: 28px;
  line-height: 1;
  margin-bottom: 6px;
}

@media (max-width: 899px), (max-height: 520px) {
  /* 顶部：倒计时往左贴，去掉中间的 gap 占位 */
  .island-play__top-line {
    gap: 8px;
    justify-content: flex-start;
  }
  .island-play__top-line .top-actions {
    margin-left: auto;
  }
  .timer {
    padding: 6px 12px;
    font-size: 14px;
    margin-left:30px;
  }

  /* 工具栏：横向滚动条从底部移到顶部 */
  .terrain-toolbar__tools {
    padding: 6px 4px 4px;
    scrollbar-position: top;
  }

  .terrain-toolbar__tools::-webkit-scrollbar {
    height: 5px;
  }

  .terrain-tool__icon {
    width: 36px;
    height: 36px;
  }
  .terrain-tool__icon svg {
    width: 26px;
    height: 26px;
  }
  .terrain-tool__label {
    font-size: 10px;
  }
  .terrain-material__swatch {
    width: 30px;
    height: 30px;
    border-width: 1.5px;
  }
  .terrain-material__label {
    font-size: 9px;
  }
  .terrain-toolbar {
    min-height: 64px;
    padding: 4px 8px 6px;
    gap: 4px;
  }
  .terrain-toolbar__back {
    width: 34px;
    height: 34px;
  }
  .terrain-toolbar__tools {
    gap: 2px;
  }
  .terrain-tool {
    min-width: 46px;
    padding: 2px 2px 4px;
    gap: 2px;
  }
  .terrain-material-palette {
    gap: 3px;
    padding-left: 2px;
  }
  .terrain-dock--open {
    max-width: calc(100vw - 80px);
  }
  .terrain-dock--material {
    max-width: calc(100vw - 60px);
  }
  .dock-btn {
    min-width: 64px;
    min-height: 64px;
    padding: 6px 8px;
    font-size: 13px;
  }
  .dock-btn__ic {
    font-size: 22px;
    margin-bottom: 3px;
  }
}

/* 横屏提示 */
.landscape-prompt {
  position: fixed;
  inset: 0;
  z-index: 9999;
  background: rgba(8, 18, 30, 0.78);
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
}

.landscape-prompt__card {
  background: linear-gradient(180deg, #ffffff 0%, #f3f7fb 100%);
  border-radius: 18px;
  padding: 28px 28px 22px;
  width: min(320px, 86vw);
  text-align: center;
  box-shadow: 0 14px 40px rgba(0, 0, 0, 0.32);
  color: #1f2c3c;
  font-family: inherit;
}

.landscape-prompt__icon {
  width: 96px;
  height: 96px;
  margin: 0 auto 14px;
  position: relative;
}

.landscape-prompt__phone {
  position: absolute;
  left: 50%;
  top: 50%;
  width: 38px;
  height: 64px;
  border: 3px solid #2e7df6;
  border-radius: 7px;
  transform: translate(-50%, -50%);
  animation: landscape-phone-tilt 2.4s ease-in-out infinite;
  background: rgba(46, 125, 246, 0.08);
}

.landscape-prompt__phone::after {
  content: '';
  position: absolute;
  left: 50%;
  bottom: 4px;
  width: 18px;
  height: 2px;
  border-radius: 1px;
  background: #2e7df6;
  transform: translateX(-50%);
}

.landscape-prompt__arrow {
  position: absolute;
  left: 50%;
  top: 50%;
  width: 64px;
  height: 64px;
  border: 3px solid #2e7df6;
  border-radius: 50%;
  border-left-color: transparent;
  border-top-color: transparent;
  transform: translate(-50%, -50%) rotate(45deg);
  animation: landscape-arrow-rotate 2.4s linear infinite;
}

.landscape-prompt__title {
  font-size: 18px;
  font-weight: 700;
  margin-bottom: 6px;
  letter-spacing: 0.5px;
}

.landscape-prompt__hint {
  font-size: 13px;
  color: #5a6776;
  margin-bottom: 8px;
  line-height: 1.5;
}

.landscape-prompt__sub {
  font-size: 12px;
  color: #8a96a4;
  margin-bottom: 18px;
  line-height: 1.5;
}

.landscape-prompt__btn {
  appearance: none;
  border: none;
  background: #2e7df6;
  color: #fff;
  padding: 9px 18px;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.5px;
  cursor: pointer;
  transition: background 0.18s ease, transform 0.18s ease;
  display: block;
  width: 100%;
  margin-bottom: 8px;
}

.landscape-prompt__btn:hover {
  background: #1f6be0;
  transform: translateY(-1px);
}

.landscape-prompt__btn:active {
  transform: translateY(0);
}

.landscape-prompt__btn--ghost {
  background: transparent;
  color: #5a6776;
  border: 1px solid #c5cdd6;
}

.landscape-prompt__btn--ghost:hover {
  background: rgba(46, 125, 246, 0.08);
  color: #2e7df6;
  border-color: #2e7df6;
}

.landscape-fade-enter-active,
.landscape-fade-leave-active {
  transition: opacity 0.28s ease;
}
.landscape-fade-enter-from,
.landscape-fade-leave-to {
  opacity: 0;
}

@keyframes landscape-phone-tilt {
  0%, 100% { transform: translate(-50%, -50%) rotate(0deg); }
  40% { transform: translate(-50%, -50%) rotate(90deg); }
  60% { transform: translate(-50%, -50%) rotate(90deg); }
}

@keyframes landscape-arrow-rotate {
  to { transform: translate(-50%, -50%) rotate(405deg); }
}

/* 保存成功感谢弹窗 */
.save-success-overlay {
  position: fixed;
  inset: 0;
  z-index: 1400;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background: rgba(8, 24, 48, 0.65);
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
}

.save-success-card {
  width: min(420px, 90vw);
  padding: 36px 28px 32px;
  border-radius: 20px;
  background: linear-gradient(165deg, #ffffff 0%, #f0f7ff 100%);
  box-shadow: 0 20px 60px rgba(13, 71, 161, 0.35);
  text-align: center;
  box-sizing: border-box;
}

.save-success-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 72px;
  height: 72px;
  margin: 0 auto 20px;
  border-radius: 50%;
  background: linear-gradient(145deg, #e8f5e9 0%, #c8e6c9 100%);
  color: #2e7d32;
}

.save-success-message {
  margin: 0;
  padding: 0;
  font-size: 17px;
  line-height: 1.65;
  color: #1a237e;
  font-weight: 500;
  letter-spacing: 0.02em;
}

.save-success-enter-active,
.save-success-leave-active {
  transition: opacity 0.35s ease;
}

.save-success-enter,
.save-success-leave-to {
  opacity: 0;
}

/* 隐私蒙层容器 */
.privacy-wrapper {
  position: fixed;
  inset: 0;
  pointer-events: none;
  z-index: 998;
}

/* 隐私蒙层 */
.privacy-overlay {
  position: fixed;
  inset: 0;
  z-index: 999;
  pointer-events: auto;
  background: rgba(0, 0, 0, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
}

.privacy-bg {
  max-width: 90%;
  max-height: 90%;
  object-fit: contain;
  pointer-events: none;
}

.privacy-bg--sm {
  max-width: 80%;
  max-height: 45%;
  position: absolute;
  right: 10px;
  top: 160px;
}

.privacy-bg--step2-tool {
  max-width: 80%;
  max-height: 45%;
  position: absolute;
  left: 170px;
  bottom: 20px;
}
@media (max-width: 899px), (max-height: 520px) {
  .privacy-bg--step2-tool {
    max-width: 80%;
    max-height: 45%;
    position: absolute;
    left: 100px;
    bottom: 30px;
  }
}
.privacy-bg--step3-terrain {
  max-width: 80%;
  max-height: 45%;
  position: absolute;
  left: 120px;
  bottom: 120px;
}

@media (max-width: 899px), (max-height: 520px) {
  .privacy-bg--step3-terrain {
    max-width: 60%;
    max-height: 45%;
    position: absolute;
    left: 120px;
    bottom: 100px;
}
  .privacy-bg--sm {
    max-width: 80%;
  max-height: 45%;
  position: absolute;
  bottom: 30px;
  right: 160px;
  }
}

.privacy-bg--step4 {
  position: absolute;
    right: 10px;
    max-width: 86%;
}

@media (max-width: 899px), (max-height: 520px) {
  .privacy-bg--step4 {
    max-width: 80%;
  max-height: 45%;
  position: absolute;
  bottom: 30px;
  right: 160px;
  }
}

.privacy-bg--step5 {
  position: absolute;
    left: 500px;
    max-width: 80%;
}

@media (max-width: 899px), (max-height: 520px) {
  .privacy-bg--step5 {
    position: absolute;
        bottom: 90px;
        left: 200px;
        max-width: 55%;
  }
}

.privacy-next-btn {
  position: fixed;
  top: 20px;
  right: 20px;
  width: 80px;
  height: auto;
  cursor: pointer;
  z-index: 1000;
}

/* 蒙层过渡动画 */
.privacy-fade-enter-active,
.privacy-fade-leave-active {
  transition: opacity 0.25s ease;
}

.privacy-fade-enter-from,
.privacy-fade-leave-to {
  opacity: 0;
  pointer-events: none;
}

.privacy-img-fade-enter-active,
.privacy-img-fade-leave-active {
  transition: opacity 0.2s ease;
}

.privacy-img-fade-enter-from,
.privacy-img-fade-leave-to {
  opacity: 0;
}
</style>



// WEBPACK FOOTER //
// src/views/IslandPlay.vue